import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { uploadDocumentWithDedupe } from '../utils/uploadDocument';
import PharmaEnrichButton from '../components/PharmaEnrichButton';
import { InterestTagger } from '../components/InterestTagger';
import { StructuredFitPanel } from '../components/StructuredFitPanel';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Building2, Mail, Users, Briefcase, Send, User, Plus, Search, Loader2, FileText, Upload, Trash2, Shield, Download, FileSignature, Pencil } from 'lucide-react';
import { CopyEmail } from '@/components/CopyEmail';
import { CountrySelect } from '@/components/CountrySelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import BDRoutingRulesCard from '@/components/BDRoutingRulesCard';
import { GitMerge, X as IconX } from 'lucide-react';

const API = '/api';

const OUTREACH_COLORS = {
  'Pipeline': 'bg-slate-100 text-slate-600',
  'Active': 'bg-sky-100 text-sky-700',
  'Introduction Made': 'bg-cyan-100 text-cyan-700',
  'Email Acknowledged': 'bg-lime-100 text-lime-700',
  'In Negotiation': 'bg-purple-100 text-purple-700',
  'Asset Under Evaluation': 'bg-amber-100 text-amber-700',
  'Meeting Requested': 'bg-violet-50 text-violet-600',
  'Meeting Scheduled': 'bg-violet-100 text-violet-700',
  'CDA in Progress': 'bg-blue-50 text-blue-600',
  'CDA Signed': 'bg-blue-100 text-blue-700',
  'Due Diligence': 'bg-indigo-100 text-indigo-700',
  'Deal Discussion': 'bg-purple-100 text-purple-700',
  'Closed - Successful': 'bg-emerald-100 text-emerald-700',
  'Declined': 'bg-rose-100 text-rose-700',
  'Awaiting Trial Data': 'bg-orange-100 text-orange-700',
};

export default function CompanyProfileDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [domainMismatches, setDomainMismatches] = useState([]);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveChecked, setMoveChecked] = useState(new Set());
  const [moving, setMoving] = useState(false);
  const [moveSkipped, setMoveSkipped] = useState([]);
  const [personReviewOpen, setPersonReviewOpen] = useState(false);
  const [bestContacts, setBestContacts] = useState({});
  const [recoveringContacts, setRecoveringContacts] = useState(false);
  const deleteEmployee = async (c) => {
    if (!window.confirm(`Delete ${c.name} from ${profile?.company_name}?\n\nThe identity is retained in the suppression registry so no import, AI mapping or enrichment can silently re-add them.`)) return;
    const optOut = window.confirm('Did this person explicitly request NOT to be contacted (opt-out)?\n\nOK = Yes, mark as DO NOT CONTACT (never auto-restored)\nCancel = No, just manually removed (restorable)');
    try {
      await axios.delete(`${API}/pharma-contacts/${c.id}?suppress_status=${optOut ? 'DO_NOT_CONTACT' : 'MANUALLY_REMOVED'}`, { withCredentials: true });
      toast.success(optOut ? `${c.name} removed and marked Do Not Contact` : `${c.name} deleted — retained in suppression registry`);
      fetchProfile();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete contact'); }
  };
  const [personResolving, setPersonResolving] = useState('');
  const resolvePerson = async (pm, action, contactId) => {
    setPersonResolving(pm.name);
    try {
      await axios.post(`${API}/pharma-enrich/company_profile/${id}/resolve-person`,
        { name: pm.name, document: pm.document, action, contact_id: contactId }, { withCredentials: true });
      toast.success(action === 'merge' ? `Merged into existing employee` : action === 'create' ? `${pm.name} created as new employee` : 'Dismissed');
      fetchProfile();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Resolve failed'); }
    finally { setPersonResolving(''); }
  };
  const [loading, setLoading] = useState(true);
  // Merge Company — mirror of the biotech CompanyDetailPage flow. Lets
  // the user consolidate "Cipla" + "Cipla Ltd" + "Cipla US" into a
  // single master profile. Feb 2026.
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeQuery, setMergeQuery] = useState('');
  const [mergeResults, setMergeResults] = useState([]);
  const [mergeSearching, setMergeSearching] = useState(false);
  const [mergePicked, setMergePicked] = useState({}); // {profileId: profile}
  const [merging, setMerging] = useState(false);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [researching, setResearching] = useState(false);
  const [deepResearching, setDeepResearching] = useState(false);
  const emptyEmployee = { name: '', email: '', phone: '', designation: '', department: '', location: '', interest_areas: [] };
  const [employeeForm, setEmployeeForm] = useState({ ...emptyEmployee });
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deckUploading, setDeckUploading] = useState(false);
  // NDA & Agreements (between us and this pharma/distributor)
  const [agreements, setAgreements] = useState([]);
  const [agreementDocType, setAgreementDocType] = useState('NDA');
  const [agreementCustomLabel, setAgreementCustomLabel] = useState('');
  const [agreementStatus, setAgreementStatus] = useState('Signed');
  const [agreementSignedDate, setAgreementSignedDate] = useState('');
  const [agreementNotes, setAgreementNotes] = useState('');
  const [agreementUploading, setAgreementUploading] = useState(false);
  const agreementFileRef = React.useRef(null);
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [policyForm, setPolicyForm] = useState({ mode: 'all', poc_email: '', poc_name: '', portal_url: '', portal_name: '', note: '' });
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [signalDialogOpen, setSignalDialogOpen] = useState(false);
  const [signalForm, setSignalForm] = useState({ area: '', status: 'under_review', source_contact_id: '', source_contact_name: '', source_contact_email: '', evidence: '' });
  const [savingSignal, setSavingSignal] = useState(false);
  // AI-paste signal parser state
  const [signalPasteText, setSignalPasteText] = useState('');
  const [parsingSignal, setParsingSignal] = useState(false);

  // ── Match Assets by Prompt (company-level) ───────────────────────────────
  // Mirrors the contact-level flow on PharmaContactDetailPage but works off
  // this profile's `therapeutic_interests` + `disease_areas`. Lives here so
  // commercialization partners (which often have zero contacts) can still
  // scout assets without the user having to drill into a sub-contact.
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [matchPrompt, setMatchPrompt] = useState('');
  const [matchRunning, setMatchRunning] = useState(false);
  const [matchResults, setMatchResults] = useState(null);

  const runMatchByPrompt = async () => {
    const text = (matchPrompt || '').trim();
    if (!text) { toast.error('Paste or type a scouting brief first'); return; }
    setMatchRunning(true);
    setMatchResults(null);
    try {
      const { data } = await axios.post(
        `${API}/ai/match-company-assets-by-prompt`,
        { company_profile_id: id, prompt: text },
        { withCredentials: true },
      );
      setMatchResults(data);
      if ((data.total_matches || 0) === 0) {
        toast.message('No matches', { description: 'No assets matched the brief. Try a broader prompt.' });
      } else {
        toast.success(`${data.total_matches} asset${data.total_matches === 1 ? '' : 's'} matched`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Match failed');
    } finally {
      setMatchRunning(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/company-profiles/${id}`, { withCredentials: true });
      setProfile(res.data);
      // Domain-mismatch banner: judge the contacts ACTUALLY listed on this
      // page (the list is broad — brand tokens / stale employer names), so a
      // @gene.com person showing under Roche is flagged regardless of what
      // their company_name field says (Roche × Genentech, Oct 2026).
      axios.get(`${API}/corporate-groups/domain-audit`, { withCredentials: true })
        .then(r => {
          const rules = r.data?.rules || {};
          const resolved = r.data?.resolved_profiles || {};
          const pageName = (res.data?.company_name || '').toLowerCase();
          const pageCanon = /genentech/.test(pageName) ? 'Genentech' : /roche/.test(pageName) ? 'Roche' : null;
          const ruleFor = (c) => {
            const dom = (c.email || '').toLowerCase().split('@')[1] || '';
            return Object.entries(rules).find(([d]) => dom === d || dom.endsWith(`.${d}`))?.[1];
          };
          const listed = (res.data?.contacts || []).filter(c => {
            const rule = ruleFor(c);
            if (!rule || !pageCanon || rule.company === pageCanon || c.affiliation_override) return false;
            const target = resolved[rule.company];
            return !(target && c.company_profile_id === target.id);
          }).map(c => {
            const rule = ruleFor(c);
            return {
              id: c.id, name: c.name, email: c.email, designation: c.designation || '',
              company_name: res.data?.company_name, expected_company: resolved[rule.company]?.company_name || rule.company,
            };
          });
          setDomainMismatches(listed);
        })
        .catch(() => {});
    } catch {
      toast.error('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, [id]);

  // Debounced search for potential duplicate pharma profiles. Mirrors
  // the biotech merge search — hits the existing `/company-profiles`
  // list endpoint with the typed query. Feb 2026.
  useEffect(() => {
    if (!mergeDialogOpen) return;
    const q = (mergeQuery || '').trim();
    if (!q || q.length < 2) { setMergeResults([]); return; }
    let cancelled = false;
    setMergeSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/company-profiles`, {
          params: { search: q, limit: 30 },
          withCredentials: true,
        });
        if (cancelled) return;
        const list = (data.items || data.company_profiles || (Array.isArray(data) ? data : []))
          .filter(p => p.id !== id);
        setMergeResults(list);
      } catch {
        if (!cancelled) setMergeResults([]);
      } finally {
        if (!cancelled) setMergeSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [mergeQuery, mergeDialogOpen, id]);

  const handleConfirmPharmaMerge = async () => {
    const dropIds = Object.keys(mergePicked);
    if (dropIds.length === 0) {
      toast.error('Pick at least one duplicate to merge into this master');
      return;
    }
    setMerging(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/merge-pharma-companies`,
        { survivor_id: id, drop_ids: dropIds },
        { withCredentials: true },
      );
      const moved = Object.entries(data.collections_moved || {})
        .filter(([, v]) => typeof v === 'number' && v > 0)
        .map(([k, v]) => `${v} ${k.split('.').pop()}`).join(' · ');
      const failed = data.failed_count || 0;
      toast.success(
        `Merged ${data.merged_count} pharma profile${data.merged_count === 1 ? '' : 's'} into ${profile?.company_name || 'master'}${moved ? ` · ${moved}` : ''}${failed ? ` · ${failed} failed` : ''}`,
        { duration: 8000 },
      );
      setMergeDialogOpen(false);
      try { window.location.reload(); } catch { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Pharma merge failed');
    } finally {
      setMerging(false);
    }
  };

  const openEditEmployee = (c) => {
    setEditingEmployee(c);
    setEmployeeForm({
      name: c.name || '',
      email: c.email || '',
      phone: c.phone || '',
      designation: c.designation || '',
      department: c.department || '',
      location: c.location || '',
      interest_areas: c.interest_areas || [],
    });
    setEmployeeDialogOpen(true);
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await axios.put(`${API}/pharma-contacts/${editingEmployee.id}`, {
          name: employeeForm.name,
          email: employeeForm.email || '',
          phone: employeeForm.phone || '',
          designation: employeeForm.designation || '',
          department: employeeForm.department || '',
          location: employeeForm.location || '',
          company_name: editingEmployee.company_name || profile.company_name,
          company_type: editingEmployee.company_type || profile.company_type || 'Big Pharma',
          linkedin_url: editingEmployee.linkedin_url || '',
          interest_areas: editingEmployee.interest_areas || [],
          disease_areas: editingEmployee.disease_areas || {},
          excluded_therapeutic_areas: editingEmployee.excluded_therapeutic_areas || [],
          preferred_stages: editingEmployee.preferred_stages || [],
          preferred_modalities: editingEmployee.preferred_modalities || [],
          notes: editingEmployee.notes || '',
        }, { withCredentials: true });
        toast.success('Contact updated');
      } else {
        await axios.post(`${API}/pharma-contacts`, {
          ...employeeForm,
          company_name: profile.company_name,
          company_type: profile.company_type || 'Big Pharma',
          location: employeeForm.location || '',
        }, { withCredentials: true });
        toast.success('Employee added');
      }
      setEmployeeDialogOpen(false);
      setEmployeeForm({ ...emptyEmployee });
      setEditingEmployee(null);
      fetchProfile();
    } catch {
      toast.error(editingEmployee ? 'Failed to update contact' : 'Failed to add employee');
    }
  };

  const handleResearch = async () => {
    setResearching(true);
    try {
      const res = await axios.post(`${API}/company-profiles/${id}/research`, {}, { withCredentials: true });
      const newCount = res.data.new_count || 0;
      toast.success(newCount > 0 ? `Found ${newCount} new therapeutic area${newCount > 1 ? 's' : ''}` : 'No new areas found — already up to date');
      fetchProfile();
    } catch {
      toast.error('Research failed — check API key');
    } finally {
      setResearching(false);
    }
  };

  const handleDeepResearch = async () => {
    setDeepResearching(true);
    try {
      const res = await axios.post(`${API}/company-profiles/${id}/deep-research`, {}, { withCredentials: true });
      const newAreas = (res.data.new_interests_added || []).length;
      const mechs = (res.data.pipeline_mechanisms || []).length;
      toast.success(
        `Deep research complete — archetype: ${res.data.company_archetype || 'unknown'}, ` +
        `${mechs} pipeline mechanism${mechs === 1 ? '' : 's'}, ${newAreas} new TA${newAreas === 1 ? '' : 's'}`
      );
      fetchProfile();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Deep research failed — check Perplexity key';
      toast.error(msg);
    } finally {
      setDeepResearching(false);
    }
  };

  const handleDeckUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!['.pdf', '.ppt', '.pptx'].includes(ext)) { toast.error('Only PDF, PPT, and PPTX files allowed'); return; }
    setDeckUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/company-profiles/${id}/upload-deck`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Corporate deck uploaded');
      fetchProfile();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setDeckUploading(false); e.target.value = ''; }
  };

  const handleDeleteDeck = async (filename) => {
    if (!window.confirm(`Remove "${filename}"?`)) return;
    try {
      await axios.delete(`${API}/company-profiles/${id}/delete-deck`, { data: { filename }, withCredentials: true });
      toast.success('Deck removed');
      fetchProfile();
    } catch { toast.error('Failed to remove deck'); }
  };

  // ── NDA & Agreements (between our firm and this pharma/distributor) ─────
  const fetchAgreements = async () => {
    if (!id) return;
    try {
      const res = await axios.get(
        `${API}/documents?entity_type=company_profile&entity_id=${id}`,
        { withCredentials: true },
      );
      // Defensive: keep only NDA / Service Agreement / Other rows. Other doc
      // types attached to this profile (corporate decks, etc.) live elsewhere.
      const items = (res.data || []).filter(d => ['NDA', 'Service Agreement', 'Other'].includes(d.doc_type));
      setAgreements(items);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { fetchAgreements(); /* eslint-disable-next-line */ }, [id]);

  const handleAgreementUpload = async (e) => {
    const file = e?.target?.files?.[0];
    const isTrackerOnly = !file;
    if (!isTrackerOnly && file) {
      const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
      if (!['.pdf', '.doc', '.docx'].includes(ext)) {
        toast.error('Only PDF, DOC, or DOCX files allowed');
        if (agreementFileRef.current) agreementFileRef.current.value = '';
        return;
      }
    }
    const finalDocType = agreementDocType === 'Other'
      ? (agreementCustomLabel.trim() || 'Other')
      : agreementDocType;
    setAgreementUploading(true);
    try {
      const formData = new FormData();
      if (file) formData.append('file', file);
      formData.append('entity_type', 'company_profile');
      formData.append('entity_id', id);
      // We store the canonical type AND surface the user-chosen label in notes
      // when "Other" is picked (so the dedicated section can still filter by
      // ['NDA', 'Service Agreement', 'Other']).
      formData.append('doc_type', agreementDocType === 'Other' ? 'Other' : agreementDocType);
      formData.append('status', agreementStatus);
      formData.append('signed_date', agreementSignedDate);
      formData.append('notes', agreementDocType === 'Other' && agreementCustomLabel.trim()
        ? `${agreementCustomLabel.trim()}${agreementNotes ? ' — ' + agreementNotes : ''}`
        : agreementNotes);
      const { data: uploadedDoc, skipped } = await uploadDocumentWithDedupe(formData);
      if (skipped) {
        setAgreementUploading(false);
        return;
      }
      toast.success(isTrackerOnly ? `${finalDocType} tracker added` : `${finalDocType} uploaded`);
      // Optimistic prepend so the row appears instantly.
      setAgreements(prev => [uploadedDoc, ...prev]);
      // Reset form
      setAgreementCustomLabel('');
      setAgreementSignedDate('');
      setAgreementNotes('');
      if (agreementFileRef.current) agreementFileRef.current.value = '';
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setAgreementUploading(false);
    }
  };

  const handleAgreementStatusChange = async (docId, newStatus) => {
    // Optimistic UI flip
    setAgreements(prev => prev.map(d => d.id === docId ? { ...d, status: newStatus } : d));
    try {
      await axios.patch(`${API}/documents/${docId}`, { status: newStatus }, { withCredentials: true });
    } catch {
      toast.error('Failed to update status');
      fetchAgreements();
    }
  };

  const handleDeleteAgreement = async (docId, label) => {
    if (!window.confirm(`Delete the ${label} entry?`)) return;
    try {
      await axios.delete(`${API}/documents/${docId}`, { withCredentials: true });
      setAgreements(prev => prev.filter(d => d.id !== docId));
      toast.success('Entry deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleDownloadAgreement = async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err?.response?.status === 404
        ? 'No file attached — this is a tracker-only entry.'
        : 'Download failed';
      toast.error(msg);
    }
  };

  const openPolicyDialog = () => {
    const p = profile.routing_policy || {};
    setPolicyForm({
      mode: p.mode || 'all',
      poc_email: p.poc_email || '',
      poc_name: p.poc_name || '',
      portal_url: p.portal_url || '',
      portal_name: p.portal_name || '',
      note: p.note || '',
    });
    setPolicyDialogOpen(true);
  };

  const handleSavePolicy = async () => {
    if (policyForm.mode === 'single_poc' && !policyForm.poc_email.trim()) {
      toast.error('POC email is required for Single POC mode');
      return;
    }
    if (policyForm.mode === 'portal_only' && !policyForm.portal_url.trim()) {
      toast.error('Portal URL is required for Portal Only mode');
      return;
    }
    setSavingPolicy(true);
    try {
      await axios.put(`${API}/company-profiles/${id}/routing-policy`, {
        mode: policyForm.mode,
        poc_email: policyForm.poc_email.trim(),
        poc_name: policyForm.poc_name.trim(),
        portal_url: policyForm.portal_url.trim(),
        portal_name: policyForm.portal_name.trim(),
        note: policyForm.note.trim(),
      }, { withCredentials: true });
      const label = policyForm.mode === 'single_poc' ? `Locked to ${policyForm.poc_name || policyForm.poc_email}`
                  : policyForm.mode === 'portal_only' ? 'Portal-only submission configured'
                  : 'Routing policy cleared';
      toast.success(label);
      setPolicyDialogOpen(false);
      fetchProfile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save policy');
    } finally {
      setSavingPolicy(false);
    }
  };

  const handleDeletePolicy = async () => {
    if (!window.confirm('Remove BD routing policy? All contacts at this pharma will become reachable again.')) return;
    setSavingPolicy(true);
    try {
      await axios.put(`${API}/company-profiles/${id}/routing-policy`, {
        mode: 'all',
        poc_email: '', poc_name: '',
        portal_url: '', portal_name: '',
        note: '',
      }, { withCredentials: true });
      toast.success('Routing policy removed — open routing restored');
      setPolicyDialogOpen(false);
      fetchProfile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove policy');
    } finally {
      setSavingPolicy(false);
    }
  };

  const handlePickPocFromContacts = (email) => {
    const c = (profile?.contacts || []).find(x => (x.email || '').toLowerCase() === email.toLowerCase());
    setPolicyForm(prev => ({
      ...prev,
      poc_email: email,
      poc_name: c?.name || prev.poc_name,
    }));
  };

  // ===== Stage Gate (min development stage a pharma accepts for in-licensing) =====
  const [stageGateDialogOpen, setStageGateDialogOpen] = useState(false);
  const [stageGateForm, setStageGateForm] = useState({ min_stage: '', note: '' });
  const [savingStageGate, setSavingStageGate] = useState(false);

  const openStageGateDialog = () => {
    const g = profile?.stage_gate || {};
    setStageGateForm({ min_stage: g.min_stage || '', note: g.note || '' });
    setStageGateDialogOpen(true);
  };

  const handleSaveStageGate = async () => {
    setSavingStageGate(true);
    try {
      await axios.put(`${API}/company-profiles/${id}/stage-gate`, {
        min_stage: stageGateForm.min_stage,
        note: stageGateForm.note.trim(),
      }, { withCredentials: true });
      toast.success(stageGateForm.min_stage ? `Stage gate set to ${stageGateForm.min_stage}+` : 'Stage gate cleared');
      setStageGateDialogOpen(false);
      fetchProfile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save stage gate');
    } finally {
      setSavingStageGate(false);
    }
  };

  const handleDeleteStageGate = async () => {
    if (!window.confirm('Remove stage gate? This pharma will be considered for all asset stages again.')) return;
    setSavingStageGate(true);
    try {
      await axios.put(`${API}/company-profiles/${id}/stage-gate`, { min_stage: '', note: '' }, { withCredentials: true });
      toast.success('Stage gate removed');
      setStageGateDialogOpen(false);
      fetchProfile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove stage gate');
    } finally {
      setSavingStageGate(false);
    }
  };

  // ===== Manual Franchise Anchors (v27 · Feb 2026) =====
  // Lets user type real product names per TA when Deep Research hasn't
  // populated `franchise_anchors`. Feeds directly into the Matchmaker
  // Evidence Pack — critical for unblocking thin Path B pharmas.
  const [anchorDialogOpen, setAnchorDialogOpen] = useState(false);
  const [anchorRows, setAnchorRows] = useState([]);   // [{ta, products_text}]
  const [anchorNote, setAnchorNote] = useState('');
  const [savingAnchors, setSavingAnchors] = useState(false);

  const openAnchorDialog = () => {
    const existing = profile?.franchise_anchors || {};
    const rows = Object.keys(existing).length
      ? Object.entries(existing).map(([ta, products]) => ({
          ta,
          products_text: (Array.isArray(products) ? products : []).join(', '),
        }))
      : [{ ta: '', products_text: '' }];
    setAnchorRows(rows);
    setAnchorNote(profile?.franchise_anchors_manual?.note || '');
    setAnchorDialogOpen(true);
  };

  const handleSaveAnchors = async () => {
    // Build anchors payload — skip empty rows.
    const anchors = {};
    for (const r of anchorRows) {
      const ta = (r.ta || '').trim();
      if (!ta) continue;
      const products = (r.products_text || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      if (products.length) anchors[ta] = products;
    }
    setSavingAnchors(true);
    try {
      await axios.put(`${API}/company-profiles/${id}/franchise-anchors`, {
        anchors, note: anchorNote.trim(),
      }, { withCredentials: true });
      toast.success(`Saved ${Object.keys(anchors).length} franchise anchor row(s)`);
      setAnchorDialogOpen(false);
      fetchProfile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save franchise anchors');
    } finally {
      setSavingAnchors(false);
    }
  };

  const openSignalDialog = () => {
    setSignalForm({ area: '', status: 'under_review', source_contact_id: '', source_contact_name: '', source_contact_email: '', evidence: '' });
    setSignalPasteText('');
    setSignalDialogOpen(true);
  };

  const handleParseSignal = async () => {
    const text = signalPasteText.trim();
    if (!text) { toast.error('Paste the email or statement first'); return; }
    if (text.length < 20) { toast.error('Too short — paste a longer block of text'); return; }
    setParsingSignal(true);
    try {
      const { data } = await axios.post(
        `${API}/company-profiles/${id}/parse-signal`,
        {
          raw_text: text,
          source_contact_id: signalForm.source_contact_id,
          source_contact_name: signalForm.source_contact_name,
          source_contact_email: signalForm.source_contact_email,
          auto_save: true,
        },
        { withCredentials: true },
      );
      const count = data?.count || 0;
      if (count === 0) {
        toast.info('No clear signals detected in that text. Try the manual form below.');
      } else {
        const neg = (data.entries || []).filter(e => e.polarity === 'negative').length;
        const pos = count - neg;
        toast.success(`Logged ${pos} positive + ${neg} negative signal${count !== 1 ? 's' : ''}`);
        setSignalDialogOpen(false);
        await fetchProfile();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI parser failed');
    } finally {
      setParsingSignal(false);
    }
  };

  const handlePickSourceContact = (contactId) => {
    const c = (profile?.contacts || []).find(x => x.id === contactId);
    if (!c) return;
    setSignalForm(prev => ({
      ...prev,
      source_contact_id: c.id,
      source_contact_name: c.name || '',
      source_contact_email: c.email || '',
    }));
  };

  const handleSaveSignal = async () => {
    if (!signalForm.area.trim()) {
      toast.error('Therapeutic area is required');
      return;
    }
    setSavingSignal(true);
    try {
      const res = await axios.post(`${API}/company-profiles/${id}/emerging-interests`, {
        area: signalForm.area.trim(),
        status: signalForm.status,
        source_contact_id: signalForm.source_contact_id,
        source_contact_name: signalForm.source_contact_name,
        source_contact_email: signalForm.source_contact_email,
        evidence: signalForm.evidence.trim(),
      }, { withCredentials: true });
      if (res.data?.was_updated) {
        toast.success(`Refreshed existing signal: ${signalForm.area}`);
      } else {
        toast.success(`Logged emerging interest: ${signalForm.area}`);
      }
      setSignalDialogOpen(false);
      fetchProfile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save signal');
    } finally {
      setSavingSignal(false);
    }
  };

  const handleDeleteSignal = async (entryId) => {
    if (!window.confirm('Remove this emerging interest signal?')) return;
    try {
      await axios.delete(`${API}/company-profiles/${id}/emerging-interests/${entryId}`, { withCredentials: true });
      toast.success('Signal removed');
      fetchProfile();
    } catch {
      toast.error('Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">Company profile not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/company-profiles')}>Back to list</Button>
      </div>
    );
  }

  const contacts = profile.contacts || [];
  const outreach = profile.outreach || [];

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="company-profile-detail-page">
      {/* Back Button */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/company-profiles')} className="text-slate-500 hover:text-slate-700" data-testid="back-to-profiles">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {profile.company_name}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <Badge variant="outline" className="text-xs">{profile.company_type}</Badge>
            {profile.category === 'regional_distributor' && (
              <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-200">Commercial Partner</Badge>
            )}
          </div>
          {profile.corporate_group && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs text-slate-500" data-testid="corporate-group-line">
              {profile.group_role === 'member' && profile.parent_company ? (
                <>
                  <span>Member of <span className="font-medium text-slate-600">{profile.corporate_group}</span> · Parent:</span>
                  <button
                    className="text-sky-600 hover:underline font-medium"
                    data-testid="parent-company-link"
                    onClick={() => navigate(`/company-profile/${profile.parent_company.id}`)}
                  >
                    {profile.parent_company.name}
                  </button>
                </>
              ) : (
                <>
                  <span><span className="font-medium text-slate-600">{profile.corporate_group}</span>{(profile.group_companies || []).length > 0 ? ' · Group companies:' : ''}</span>
                  {(profile.group_companies || []).map((g) => (
                    <button
                      key={g.id}
                      className="text-sky-600 hover:underline font-medium"
                      data-testid={`group-company-link-${g.id}`}
                      onClick={() => navigate(`/company-profile/${g.id}`)}
                      title={g.relationship || ''}
                    >
                      {g.name}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
          {domainMismatches.length > 0 && (
            <div className="mt-3 flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2" data-testid="domain-mismatch-banner">
              <p className="text-xs text-amber-800">
                <span className="font-semibold">{domainMismatches.length} contact{domainMismatches.length === 1 ? '' : 's'}</span> filed under {profile.company_name} {domainMismatches.length === 1 ? 'has' : 'have'} <span className="font-mono">@{(domainMismatches[0].email || '').split('@')[1]}</span> email{domainMismatches.length === 1 ? '' : 's'} and likely belong{domainMismatches.length === 1 ? 's' : ''} to <span className="font-semibold">{domainMismatches[0].expected_company}</span>.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0 h-7 text-xs"
                data-testid="review-mismatch-btn"
                onClick={() => { setMoveChecked(new Set(domainMismatches.map(m => m.id))); setMoveDialogOpen(true); }}
              >
                Review &amp; Move
              </Button>
            </div>
          )}
          <Dialog open={moveDialogOpen} onOpenChange={(o) => { if (!moving) setMoveDialogOpen(o); }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="domain-move-dialog">
              <DialogHeader>
                <DialogTitle>Domain audit — move contacts to their canonical company</DialogTitle>
                <DialogDescription>
                  Corporate email domain is a strong ownership signal (@gene.com → Genentech, @roche.com → Roche). Moving preserves all tags, routing rules, outreach and email history — an audit entry is added to each contact. Contacts marked as manual overrides are never moved. Uncheck anyone with a legitimate cross-entity assignment.
                </DialogDescription>
              </DialogHeader>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-80 overflow-y-auto">
                {domainMismatches.map((m) => (
                  <label key={m.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`mismatch-row-${m.id}`}>
                    <input
                      type="checkbox"
                      checked={moveChecked.has(m.id)}
                      onChange={(e) => {
                        const next = new Set(moveChecked);
                        if (e.target.checked) next.add(m.id); else next.delete(m.id);
                        setMoveChecked(next);
                      }}
                    />
                    <div className="min-w-0">
                      <span className="font-medium text-slate-800">{m.name}</span>
                      <span className="ml-2 text-xs text-slate-500 font-mono">{m.email}</span>
                      {m.designation && <div className="text-[11px] text-slate-400 truncate">{m.designation}</div>}
                    </div>
                    <span className="ml-auto text-[11px] text-slate-500 shrink-0">
                      {m.company_name} → <span className="font-semibold text-slate-700">{m.expected_company}</span>
                    </span>
                  </label>
                ))}
              </div>
              {moveSkipped.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1" data-testid="move-skipped-list">
                  <p className="font-semibold">{moveSkipped.length} not moved</p>
                  {moveSkipped.slice(0, 8).map((s) => (
                    <p key={s.id}>{s.name || s.id} — <span className="italic">{s.reason}</span></p>
                  ))}
                  {moveSkipped.length > 8 && <p>…and {moveSkipped.length - 8} more</p>}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" disabled={moving} onClick={() => { setMoveDialogOpen(false); setMoveSkipped([]); }} data-testid="move-cancel-btn">Cancel</Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={moving || moveChecked.size === 0}
                  data-testid="move-confirm-btn"
                  onClick={async () => {
                    setMoving(true);
                    try {
                      const r = await axios.post(`${API}/corporate-groups/domain-audit/apply`,
                        { contact_ids: Array.from(moveChecked) }, { withCredentials: true });
                      const sk = r.data.skipped || [];
                      const reasons = Object.entries(sk.reduce((a, s) => { a[s.reason] = (a[s.reason] || 0) + 1; return a; }, {})).map(([k, v]) => `${v} ${k}`).join(', ');
                      if (r.data.moved) toast.success(`${r.data.moved} contact${r.data.moved === 1 ? '' : 's'} moved to ${domainMismatches[0]?.expected_company || 'the correct company'}${sk.length ? ` · skipped: ${reasons}` : ''}`);
                      else toast.warning(`Nothing moved · ${reasons}`, { duration: 8000 });
                      if ((r.data.created_profiles || []).length) toast.info(`Created missing profile: ${r.data.created_profiles.map(p => p.company_name).join(', ')}`);
                      setMoveSkipped(sk);
                      if (!sk.length) setMoveDialogOpen(false);
                      fetchProfile();
                    } catch (e) { toast.error(e?.response?.data?.detail || 'Move failed'); }
                    finally { setMoving(false); }
                  }}
                >
                  {moving ? 'Moving…' : `Move ${moveChecked.size} Contact${moveChecked.size === 1 ? '' : 's'}`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs border-sky-300 text-sky-700 hover:bg-sky-50"
            data-testid="match-by-prompt-btn"
            onClick={() => { setMatchPrompt(''); setMatchResults(null); setMatchDialogOpen(true); }}
            title="Find biotech assets matching a free-text scouting brief for this company"
          >
            <Search className="w-3.5 h-3.5 mr-1" /> Match by Prompt
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            data-testid="toggle-category-btn"
            onClick={async () => {
              const isDistributor = profile.category === 'regional_distributor';
              const next = isDistributor ? 'pharma' : 'regional_distributor';
              const msg = isDistributor
                ? `Move "${profile.company_name}" back to Pharma?`
                : `Reclassify "${profile.company_name}" as a Distributor? They'll appear under the Distributors tab instead of the Pharma tab.`;
              if (!window.confirm(msg)) return;
              try {
                await axios.patch(
                  `${API}/company-profiles/${id}/category`,
                  { category: next },
                  { withCredentials: true },
                );
                toast.success(next === 'regional_distributor' ? 'Moved to Distributors' : 'Moved back to Pharma');
                fetchProfile();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Failed to change category');
              }
            }}
          >
            {profile.category === 'regional_distributor' ? 'Move back to Pharma' : 'Move to Distributors'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="pharma-merge-btn"
            onClick={() => { setMergeDialogOpen(true); setMergeQuery(''); setMergeResults([]); setMergePicked({}); }}
            title="Merge duplicate pharma company profiles into this master record (e.g. Cipla / Cipla Ltd / Cipla US → Cipla)"
          >
            <GitMerge className="w-3.5 h-3.5 mr-1" /> Merge Company
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7 border-violet-300 text-violet-700 hover:bg-violet-50"
            data-testid="reclassify-as-biotech-btn"
            onClick={async () => {
              if (!window.confirm(
                `⚠️ Reclassify "${profile.company_name}" as a Biotech?\n\n` +
                `This will:\n` +
                `  • Create a new Biotech record (name, country, website, description, TAs preserved)\n` +
                `  • DELETE this ${profile.category === 'regional_distributor' ? 'Distributor' : 'Pharma'} profile\n` +
                `  • Routing policies / outreach preferences / partnering email will NOT carry over\n\n` +
                `Continue?`
              )) return;
              try {
                const res = await axios.post(`${API}/company-profiles/${profile.id}/move-to-biotech`, {}, { withCredentials: true });
                toast.success('Reclassified as Biotech');
                window.location.href = `/biotech/${res.data.new_id}`;
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Reclassify failed');
              }
            }}
          >
            Reclassify as Biotech
          </Button>
        </div>
      </div>

      {/* Commercial Partner intelligence (regional_distributor) */}
      {profile.category === 'regional_distributor' && (
        <div className="mb-6 border border-amber-200 bg-amber-50/40 rounded-lg p-4" data-testid="commercial-partner-intel">
          <h3 className="text-sm font-bold text-amber-800 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Commercial Partner Intelligence</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-xs">
            {(profile.licensing_territories?.length || profile.country_footprint?.length) ? (
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Licensing Territories</p>
                <div className="flex flex-wrap gap-1">
                  {(profile.licensing_territories?.length ? profile.licensing_territories : profile.country_footprint).slice(0, 20).map((c, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px] font-normal">{c}</Badge>
                  ))}
                </div>
              </div>
            ) : null}
            {profile.regions?.length ? (
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Regions</p>
                <div className="flex flex-wrap gap-1">{profile.regions.map((r, i) => <Badge key={i} variant="secondary" className="text-[10px] font-normal">{r}</Badge>)}</div>
              </div>
            ) : null}
            {(profile.therapeutic_franchises?.length || profile.therapeutic_interests?.length) ? (
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Therapeutic Franchises</p>
                <div className="flex flex-wrap gap-1">{(profile.therapeutic_franchises?.length ? profile.therapeutic_franchises : profile.therapeutic_interests).map((t, i) => <Badge key={i} variant="secondary" className="text-[10px] font-normal">{t}</Badge>)}</div>
              </div>
            ) : null}
            {profile.channels?.length ? (
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Commercial Channels</p>
                <div className="flex flex-wrap gap-1">{profile.channels.map((c, i) => <Badge key={i} variant="secondary" className="text-[10px] font-normal">{c}</Badge>)}</div>
              </div>
            ) : null}
            {profile.preferred_modalities?.length ? (
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Modalities / Product Types</p>
                <div className="flex flex-wrap gap-1">{[...(profile.preferred_modalities||[]), ...(profile.product_types||[])].map((m, i) => <Badge key={i} variant="secondary" className="text-[10px] font-normal">{m}</Badge>)}</div>
              </div>
            ) : null}
            {profile.inlicensing_appetite?.stages?.length ? (
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">In-Licensing Stages</p>
                <div className="flex flex-wrap gap-1">{profile.inlicensing_appetite.stages.map((s, i) => <Badge key={i} variant="secondary" className="text-[10px] font-normal">{s}</Badge>)}</div>
              </div>
            ) : null}
            {profile.marketed_products?.length ? (
              <div className="sm:col-span-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Marketed / In-Licensed Products</p>
                <p className="text-slate-700">{profile.marketed_products.join(' · ')}</p>
              </div>
            ) : null}
            {profile.bd_requirements ? (
              <div className="sm:col-span-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">BD Requirements</p>
                <p className="text-slate-700">{profile.bd_requirements}</p>
              </div>
            ) : null}
            {profile.recent_deals_summary ? (
              <div className="sm:col-span-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Recent Deals</p>
                <p className="text-slate-700">{profile.recent_deals_summary}</p>
              </div>
            ) : null}
          </div>
          {profile.distribution_intel_citations?.length ? (
            <p className="text-[10px] text-slate-400 mt-3">Sources: {profile.distribution_intel_citations.slice(0, 5).map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer" className="text-sky-500 hover:underline mr-2">[{i + 1}]</a>)}</p>
          ) : null}
        </div>
      )}


      {/* ═══════════════════════════════════════════════════════════════
          SECTION A · OUTREACH GOVERNANCE
          Who at this pharma gets emails for which TA, at what stage, and
          under what routing policy. This drives the actual "To:" line
          when we send outreach — NOT which assets are worth pitching.
          ═══════════════════════════════════════════════════════════════ */}
      <div className="mt-2 mb-3 flex items-center gap-3" data-testid="section-outreach-governance">
        <span className="text-[11px] font-bold text-slate-700 uppercase tracking-[0.14em]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Outreach Governance
        </span>
        <span className="text-[10.5px] text-slate-400 italic">who gets the email · P1 explicit → P3 fallback</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      {/* Partnering Email & Outreach Preference */}
      <div className="flex flex-wrap items-center gap-4 mb-6 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-testid="outreach-preference-bar">
        <div className="flex items-center gap-2 min-w-0">
          <Shield className="w-4 h-4 text-slate-400 shrink-0" />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider shrink-0">Outreach Preference:</span>
          <Select
            value={profile.outreach_preference || 'Individuals Only'}
            onValueChange={async (val) => {
              try {
                await axios.put(`${API}/company-profiles/${id}`, {
                  ...profile,
                  outreach_preference: val,
                }, { withCredentials: true });
                setProfile(prev => ({ ...prev, outreach_preference: val }));
                toast.success(`Outreach preference set to "${val}"`);
              } catch { toast.error('Failed to update'); }
            }}
          >
            <SelectTrigger className="h-7 w-[180px] text-xs border-slate-200" data-testid="outreach-preference-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Individuals Only" className="text-xs">Individuals Only</SelectItem>
              <SelectItem value="Partnering Email Only" className="text-xs">Partnering Email Only</SelectItem>
              <SelectItem value="Both" className="text-xs">Both</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-xs text-slate-500 shrink-0">Partnering Email:</span>
          {profile.partnering_email ? (
            <div className="flex items-center gap-1">
              <a href={`mailto:${profile.partnering_email}`} className="text-xs text-sky-600 hover:underline">{profile.partnering_email}</a>
              <CopyEmail email={profile.partnering_email} />
              <button onClick={async () => {
                const email = prompt('Update partnering email:', profile.partnering_email);
                if (email === null) return;
                try {
                  await axios.put(`${API}/company-profiles/${id}`, { ...profile, partnering_email: email }, { withCredentials: true });
                  setProfile(prev => ({ ...prev, partnering_email: email }));
                  toast.success(email ? 'Partnering email updated' : 'Partnering email removed');
                } catch { toast.error('Failed to update'); }
              }} className="text-[10px] text-slate-400 hover:text-slate-600 ml-1">edit</button>
            </div>
          ) : (
            <button onClick={async () => {
              const email = prompt('Enter partnering email (e.g., partnering@biogen.com):');
              if (!email) return;
              try {
                await axios.put(`${API}/company-profiles/${id}`, { ...profile, partnering_email: email }, { withCredentials: true });
                setProfile(prev => ({ ...prev, partnering_email: email }));
                toast.success('Partnering email added');
              } catch { toast.error('Failed to update'); }
            }} className="text-xs text-sky-600 hover:underline cursor-pointer" data-testid="add-partnering-email">
              + Add
            </button>
          )}
        </div>
        {profile.outreach_preference === 'Partnering Email Only' && !profile.partnering_email && (
          <span className="text-[10px] text-amber-600 font-medium">Set a partnering email to use this preference</span>
        )}
      </div>

      {/* BD Routing Policy Card */}
      <div
        className={`mb-6 rounded-lg border px-4 py-3 flex flex-wrap items-center gap-3 ${profile.routing_policy?.mode === 'single_poc' || profile.routing_policy?.mode === 'portal_only' ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'}`}
        data-testid="routing-policy-bar"
      >
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <Shield className={`w-4 h-4 shrink-0 ${profile.routing_policy?.mode === 'single_poc' || profile.routing_policy?.mode === 'portal_only' ? 'text-amber-600' : 'text-slate-400'}`} />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider shrink-0">BD Routing Policy:</span>
          {profile.routing_policy?.mode === 'single_poc' ? (
            <div className="flex items-center gap-2 text-xs">
              <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]" data-testid="routing-policy-mode-badge">Single Point of Contact</Badge>
              <span className="text-slate-700">
                Route via <span className="font-semibold">{profile.routing_policy.poc_name || profile.routing_policy.poc_email}</span>
              </span>
              {profile.routing_policy.poc_email && (
                <span className="text-slate-500">— <a href={`mailto:${profile.routing_policy.poc_email}`} className="text-sky-600 hover:underline">{profile.routing_policy.poc_email}</a></span>
              )}
            </div>
          ) : profile.routing_policy?.mode === 'portal_only' ? (
            <div className="flex items-center gap-2 text-xs flex-wrap">
              <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]" data-testid="routing-policy-mode-badge">🌐 Portal Submission Only</Badge>
              <span className="text-slate-700">Submit via</span>
              <a
                href={profile.routing_policy.portal_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
                data-testid="portal-submit-link"
              >
                {profile.routing_policy.portal_name || 'Partnering Portal'}
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
              </a>
              {profile.routing_policy.poc_name && (
                <span className="text-slate-500">— informed by <span className="font-semibold text-slate-700">{profile.routing_policy.poc_name}</span></span>
              )}
            </div>
          ) : (
            <span className="text-xs text-slate-500">Open routing — all contacts reachable</span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={openPolicyDialog}
            className="text-xs font-medium text-sky-600 hover:text-sky-800 bg-sky-50 hover:bg-sky-100 px-3 py-1.5 rounded-md transition-colors"
            data-testid="edit-routing-policy-btn"
          >
            {(profile.routing_policy?.mode === 'single_poc' || profile.routing_policy?.mode === 'portal_only') ? 'Edit' : 'Configure'}
          </button>
          {(profile.routing_policy?.mode === 'single_poc' || profile.routing_policy?.mode === 'portal_only') && (
            <button
              onClick={handleDeletePolicy}
              disabled={savingPolicy}
              className="text-xs font-medium text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-md transition-colors disabled:opacity-50"
              title="Remove routing policy"
              data-testid="delete-routing-policy-btn"
            >
              Delete
            </button>
          )}
        </div>
        {profile.routing_policy?.note && (
          <div className="w-full text-[11px] text-amber-800 italic mt-1">“{profile.routing_policy.note}”</div>
        )}
      </div>

      {/* Recipient Rules — a single workflow with two layers:
             1. Explicit rules from a BD person's email (authoritative)
             2. Contact-tag fallback (auto-derived from employee interest tags)
          The user sees one unified header; the two sub-cards below carry
          Source chips that make the layer obvious per-row. */}
      <div className="mb-2 flex items-baseline gap-3" data-testid="recipient-rules-header">
        <span className="text-[11px] font-bold text-slate-700 uppercase tracking-[0.14em]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Recipient Rules
        </span>
        <span className="text-[10.5px] text-slate-400 italic">
          who receives outreach for each TA · explicit rules first, contact-tag fallback below
        </span>
      </div>

      {/* Recipient Rules · Layer 1 — Explicit rules from BD emails */}
      <BDRoutingRulesCard
        profile={profile}
        onChanged={fetchProfile}
      />

      {/* Recipient Rules · Layer 2 — auto-derived from contact interest tags */}
      <PerTAContactsCard contacts={contacts} profile={profile} onPromoted={fetchProfile} />

      {/* Stage Gate Card (min dev-stage accepted for in-licensing) */}
      <div
        className={`mb-6 rounded-lg border px-4 py-3 flex flex-wrap items-center gap-3 ${profile.stage_gate?.min_stage ? 'bg-indigo-50 border-indigo-300' : 'bg-slate-50 border-slate-200'}`}
        data-testid="stage-gate-bar"
      >
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <svg className={`w-4 h-4 shrink-0 ${profile.stage_gate?.min_stage ? 'text-indigo-600' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider shrink-0">Stage Gate:</span>
          {profile.stage_gate?.min_stage ? (
            <div className="flex items-center gap-2 text-xs">
              <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300 text-[10px]" data-testid="stage-gate-badge">
                Only {profile.stage_gate.min_stage}+ assets
              </Badge>
              <span className="text-slate-700">AI Mapping excludes this pharma for earlier-stage assets.</span>
            </div>
          ) : (
            <span className="text-xs text-slate-500">No stage restriction — all asset stages considered.</span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={openStageGateDialog}
            className="text-xs font-medium text-sky-600 hover:text-sky-800 bg-sky-50 hover:bg-sky-100 px-3 py-1.5 rounded-md transition-colors"
            data-testid="edit-stage-gate-btn"
          >
            {profile.stage_gate?.min_stage ? 'Edit' : 'Configure'}
          </button>
          {profile.stage_gate?.min_stage && (
            <button
              onClick={handleDeleteStageGate}
              disabled={savingStageGate}
              className="text-xs font-medium text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-md transition-colors disabled:opacity-50"
              title="Remove stage gate"
              data-testid="delete-stage-gate-btn"
            >
              Delete
            </button>
          )}
        </div>
        {profile.stage_gate?.note && (
          <div className="w-full text-[11px] text-indigo-800 italic mt-1">“{profile.stage_gate.note}”</div>
        )}
      </div>

      {/* ── Manual Franchise Anchors tile (v27) ─────────────────────
          Feeds directly into the Matchmaker Evidence Pack when Deep
          Research hasn't populated franchise_anchors. */}
      <div className="mb-4 p-3 border border-emerald-200 bg-emerald-50 rounded-lg flex items-start gap-3 flex-wrap"
           data-testid="franchise-anchor-tile">
        <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
          <svg className="w-4 h-4 shrink-0 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 2l2.09 6.26L20 9l-5 3.87L16.18 20 12 16.9 7.82 20 9 12.87 4 9l5.91-.74L12 2z"/>
          </svg>
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider shrink-0">Franchise Anchors:</span>
          {profile.franchise_anchors && Object.keys(profile.franchise_anchors).length > 0 ? (
            <div className="flex items-center gap-1.5 flex-wrap text-xs">
              {Object.entries(profile.franchise_anchors).slice(0, 6).map(([ta, prods]) => (
                <Badge key={ta} className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]"
                       title={(Array.isArray(prods) ? prods : []).join(', ')}
                       data-testid={`anchor-badge-${ta}`}>
                  {ta}: {(Array.isArray(prods) ? prods : []).slice(0, 2).join(', ')}
                  {Array.isArray(prods) && prods.length > 2 ? ` +${prods.length - 2}` : ''}
                </Badge>
              ))}
              {profile.franchise_anchors_manual?.set_at && (
                <span className="text-[10px] text-emerald-800">
                  · manual edit {new Date(profile.franchise_anchors_manual.set_at).toLocaleDateString()}
                </span>
              )}
            </div>
          ) : (
            <span className="text-xs text-slate-600">
              No franchise anchors yet — the matchmaker needs at least one to write strong rationales for this pharma.
            </span>
          )}
        </div>
        <button
          onClick={openAnchorDialog}
          className="text-xs font-medium text-emerald-800 hover:text-emerald-900 bg-white hover:bg-emerald-100 border border-emerald-300 px-3 py-1.5 rounded-md transition-colors"
          data-testid="edit-franchise-anchors-btn"
        >
          {profile.franchise_anchors && Object.keys(profile.franchise_anchors).length > 0 ? 'Edit Anchors' : 'Add Anchors'}
        </button>
        {profile.franchise_anchors_manual?.note && (
          <div className="w-full text-[11px] text-emerald-800 italic mt-1">“{profile.franchise_anchors_manual.note}”</div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION B · ASSET FIT INTELLIGENCE
          Which biotech assets fit this pharma at all — auto-rolled up
          from every contact's granular Known Interests + Deep Research.
          This scores the universe of assets; it does NOT decide routing.
          ═══════════════════════════════════════════════════════════════ */}
      <div className="mt-6 mb-3 flex items-center gap-3" data-testid="section-asset-fit">
        <span className="text-[11px] font-bold text-slate-700 uppercase tracking-[0.14em]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Asset Fit Intelligence
        </span>
        <span className="text-[10.5px] text-slate-400 italic">which assets fit at all · auto-rolled from contacts + Deep Research</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      {/* Structured Fit Profile (Feb 2026 · AI Mapping v2) — rolled up
          Known Interests + Deep Research + per-contact tags → live 4-tier
          asset-fit counter with drill-down + explainability chips. */}
      <StructuredFitPanel companyName={profile?.company_name} />

      {/* Company Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Company Intelligence & Deep Research
            (Feb 2026 refactor) — chip cloud removed because those TAs
            now live under Asset Fit Intelligence · Interest Areas. This
            card keeps the Research buttons, structured tagger, notes,
            and BD Intelligence (archetype, recent BD, pipeline mechs). */}
        <Card className="border border-slate-200 shadow-sm" data-testid="profile-interests-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Company Intelligence</h3>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleResearch}
                  disabled={researching || deepResearching}
                  className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-md transition-colors disabled:opacity-50"
                  data-testid="research-company-btn"
                  title="Quick research — refreshes therapeutic areas from web search."
                >
                  {researching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                  {researching ? 'Researching...' : 'Research'}
                </button>
                <button
                  onClick={handleDeepResearch}
                  disabled={researching || deepResearching}
                  className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-md transition-colors disabled:opacity-50"
                  data-testid="deep-research-company-btn"
                  title="Deep research via Perplexity — populates Asset Fit fields (specific indications, preferred/excluded modalities, scientific preferences), pipeline mechanisms, company archetype, and recent BD summary."
                >
                  {deepResearching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                  {deepResearching ? 'Deep research...' : 'Deep research'}
                </button>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed mb-3">
              <span className="font-semibold text-slate-600">Deep Research</span> writes structured signals (specific indications, preferred/excluded modalities, scientific preferences) into the <span className="font-semibold text-indigo-600">Asset Fit Intelligence</span> panel above. Broad TA chips are shown there under {'"Interest Areas"'}.
            </p>

            {/* Feb 2026 · AI Interest Tagger. Type free-text interests
                (e.g. "gastrointestinal health and immune system") and
                Claude structures them into TA/modality/stage/geography
                tags with user approval before persisting. */}
            <div className="mt-4 pt-3 border-t border-slate-100">
              <InterestTagger
                entityType="company_profile"
                entityId={profile.id}
                initialText={profile.interest_notes || ''}
                onApplied={fetchProfile}
              />
            </div>
            {profile.notes && (
              <div className="mt-4 pt-3 border-t border-slate-100">
                <p className="text-[10px] text-slate-400 uppercase mb-1">Notes</p>
                <p className="text-sm text-slate-600 leading-relaxed">{profile.notes}</p>
              </div>
            )}

            {/* BD Intelligence — populated by Perplexity deep research. Powers
                the AI mapping moat (services/moat_intelligence.py). */}
            {(profile.pipeline_mechanisms?.length > 0 || profile.company_archetype || profile.recent_bd_summary) && (
              <div className="mt-4 pt-3 border-t border-slate-100" data-testid="bd-intelligence-block">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-[10px] text-emerald-700 uppercase tracking-wider font-semibold">BD Intelligence</p>
                  {profile.company_archetype && profile.company_archetype !== 'unknown' && (
                    <Badge
                      className={
                        'text-[10px] px-2 py-0.5 ' +
                        (profile.company_archetype === 'innovator' ? 'bg-emerald-100 text-emerald-800' :
                         profile.company_archetype === 'specialty_pharma' ? 'bg-sky-100 text-sky-800' :
                         profile.company_archetype === 'generics_inlicensor' ? 'bg-amber-100 text-amber-800' :
                         profile.company_archetype === 'emerging_market_commercial' ? 'bg-orange-100 text-orange-800' :
                         'bg-slate-100 text-slate-700')
                      }
                      data-testid="company-archetype-badge"
                    >
                      {profile.company_archetype.replace(/_/g, ' ')}
                    </Badge>
                  )}
                  {profile.bd_intel_updated_at && (
                    <span className="text-[10px] text-slate-400">
                      Updated {new Date(profile.bd_intel_updated_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                {profile.recent_bd_summary && (
                  <p className="text-xs text-slate-600 leading-relaxed mb-2" data-testid="recent-bd-summary">
                    {profile.recent_bd_summary}
                  </p>
                )}
                {profile.bd_focus_stages?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 mb-2">
                    <span className="text-[10px] text-slate-400 uppercase">BD stages:</span>
                    {profile.bd_focus_stages.map((s, i) => (
                      <Badge key={i} variant="outline" className="text-[10px] px-2 py-0.5 border-slate-200 text-slate-600">
                        {s}
                      </Badge>
                    ))}
                  </div>
                )}
                {profile.pipeline_mechanisms?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase mb-1">Pipeline mechanisms ({profile.pipeline_mechanisms.length})</p>
                    <div className="flex flex-wrap gap-1" data-testid="pipeline-mechanisms-list">
                      {profile.pipeline_mechanisms.slice(0, 24).map((m, i) => (
                        <Badge key={i} variant="secondary" className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-100">
                          {m}
                        </Badge>
                      ))}
                      {profile.pipeline_mechanisms.length > 24 && (
                        <Badge variant="secondary" className="text-[10px] px-2 py-0.5">
                          +{profile.pipeline_mechanisms.length - 24}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right column stack: Emerging Signalled Interests (moved up
            from below per user request) + Employees. Signalled Interests
            is the AI's most valuable ranking input, so it belongs
            adjacent to Company Intelligence, not buried at the bottom. */}
        <div className="space-y-6" data-testid="right-column-stack">

        {/* Emerging / Signalled Interests */}
        <Card className="border border-amber-200 bg-amber-50/30 shadow-sm" data-testid="emerging-interests-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Emerging / Signalled Interests</h3>
                <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-800">{(profile.emerging_interests || []).length}</Badge>
              </div>
              <button
                onClick={openSignalDialog}
                className="flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-md transition-colors"
                data-testid="log-signal-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                Log Signal
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mb-3">
              Revealed preferences from BD replies, conferences, or private conversations — areas this company is exploring (positive) or has explicitly declined (negative). Folded into Asset Fit Intelligence: positives boost sub-TA fit, negatives block that area.
            </p>
            {(() => {
              const all = profile.emerging_interests || [];
              const taSignals = all.filter(e => !e.source_contact_name);
              const indSignals = all.filter(e => !!e.source_contact_name);
              const renderRow = (e) => {
                const isNegative = (e.polarity || 'positive') === 'negative';
                return (
                  <div key={e.id} className={`bg-white border rounded-lg p-3 flex items-start justify-between gap-3 ${isNegative ? 'border-rose-200' : 'border-amber-200'}`} data-testid={`signal-row-${e.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isNegative ? (
                          <Badge className="bg-rose-100 text-rose-800 border-rose-300 text-xs">NOT interested · {e.area}</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs">{e.area}</Badge>
                        )}
                        {!isNegative && (
                          <Badge className="bg-slate-100 text-slate-600 text-[10px]">
                            {e.status === 'active_scouting' ? 'Active Scouting' : e.status === 'confirmed_interest' ? 'Confirmed' : 'Under Review'}
                          </Badge>
                        )}
                        {e.source_contact_name && (
                          <span className="text-[11px] text-slate-500">
                            via <span className="font-medium text-slate-700">{e.source_contact_name}</span>
                            {e.source_contact_email && ` <${e.source_contact_email}>`}
                          </span>
                        )}
                        {e.recorded_at && (
                          <span className="text-[10px] text-slate-400">
                            {new Date(e.recorded_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {e.evidence && (
                        <p className={`text-xs italic mt-1.5 leading-relaxed ${isNegative ? 'text-rose-700' : 'text-slate-600'}`}>“{e.evidence}”</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteSignal(e.id)}
                      className="text-slate-400 hover:text-rose-600 p-1"
                      data-testid={`delete-signal-${e.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              };
              // Sep 2026 (Eli Lilly / Kutoloski): contact-level exclusions used to
              // be invisible here yet could block outreach — surface them.
              const exclContacts = (profile.contacts || []).filter(c => (c.excluded_therapeutic_areas || []).length > 0);
              const exclBlock = exclContacts.length > 0 && (
                <div className="rounded-md border border-rose-200 bg-rose-50/40 p-3" data-testid="contact-level-exclusions">
                  <div className="flex items-center gap-2 mb-1.5">
                    <h4 className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">Contact-level exclusions</h4>
                    <Badge variant="secondary" className="text-[10px] bg-rose-100 text-rose-800">{exclContacts.length}</Badge>
                  </div>
                  <p className="text-[10px] text-slate-500 mb-2">Personal remits recorded on employees. They only remove that person as a recipient — the company is blocked only if they are the single BD point of contact or nobody else here is tagged for the area. Click a name to edit.</p>
                  <div className="space-y-1">
                    {exclContacts.map(c => (
                      <button key={c.id || c.email} type="button" onClick={() => openEditEmployee(c)} className="w-full text-left text-xs flex flex-wrap items-center gap-1.5 hover:bg-rose-100/60 rounded px-1.5 py-1" data-testid={`contact-exclusion-${c.id || c.email}`}>
                        <span className="font-medium text-slate-800">{c.name || c.email}</span>
                        <span className="text-slate-400">excludes</span>
                        {(c.excluded_therapeutic_areas || []).map(a => <Badge key={a} className="text-[10px] bg-white text-rose-700 border border-rose-200">{a}</Badge>)}
                        {c.updated_at && <span className="text-[10px] text-slate-400 ml-auto">{String(c.updated_at).slice(0, 10)}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              );
              if (all.length === 0) {
                return (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-400">No emerging interest signals logged yet. Log one when a contact from this company reveals interest in an off-list therapeutic area.</p>
                    {exclBlock}
                  </div>
                );
              }
              return (
                <div className="space-y-4" data-testid="signals-two-column">
                  {exclBlock}
                  {/* TA-level (no specific contact attribution) */}
                  <div data-testid="signals-ta-column">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Therapeutic Area Interest</h4>
                      <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-700">{taSignals.length}</Badge>
                    </div>
                    <p className="text-[10px] text-slate-400 mb-2">Company-wide stance — not tied to a specific contact.</p>
                    {taSignals.length > 0 ? (
                      <div className="space-y-2">{taSignals.map(renderRow)}</div>
                    ) : (
                      <p className="text-[11px] text-slate-400 italic">No company-wide TA signals yet.</p>
                    )}
                  </div>
                  {/* Individual-level (attributed to a specific contact) */}
                  <div data-testid="signals-individual-column">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Individual Preferences</h4>
                      <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-700">{indSignals.length}</Badge>
                    </div>
                    <p className="text-[10px] text-slate-400 mb-2">As communicated to us by a specific person at this company.</p>
                    {indSignals.length > 0 ? (
                      <div className="space-y-2">{indSignals.map(renderRow)}</div>
                    ) : (
                      <p className="text-[11px] text-slate-400 italic">No individual-attributed signals yet.</p>
                    )}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Employees / Contacts */}
        <Card className="border border-slate-200 shadow-sm" data-testid="profile-contacts-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Employees</h3>
                <Badge variant="secondary" className="text-[10px]">{contacts.length}</Badge>
              </div>
              <button
                onClick={() => { setEditingEmployee(null); setEmployeeForm({ ...emptyEmployee }); setEmployeeDialogOpen(true); }}
                className="flex items-center gap-1.5 text-xs font-medium text-sky-600 hover:text-sky-800 bg-sky-50 hover:bg-sky-100 px-3 py-1.5 rounded-md transition-colors"
                data-testid="add-employee-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                Add
              </button>
            </div>
            {(profile?.pending_person_matches || []).length > 0 && (
              <div className="mb-3 flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2" data-testid="pending-person-banner">
                <p className="text-[11px] text-amber-800">
                  <span className="font-semibold">{profile.pending_person_matches.length} possible existing employee match{profile.pending_person_matches.length === 1 ? '' : 'es'}</span> from document enrichment need review
                </p>
                <Button size="sm" variant="outline" className="h-6 text-[11px] border-amber-400 text-amber-800 hover:bg-amber-100" data-testid="review-person-matches-btn" onClick={() => setPersonReviewOpen(true)}>Review</Button>
              </div>
            )}
            {contacts.length > 0 ? (
              <div className="space-y-2">
                {contacts.map((c) => (
                  <a
                    key={c.id}
                    href={`/pharma/${c.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-slate-50 rounded-lg p-3 hover:bg-sky-50 transition-colors group"
                    data-testid={`employee-row-${c.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0 group-hover:bg-sky-100">
                      <User className="w-4 h-4 text-slate-500 group-hover:text-sky-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900">{c.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {c.designation && <span className="text-xs text-slate-500">{c.designation}</span>}
                        {c.email && <span className="text-xs text-slate-400 flex items-center gap-1">{c.email}<CopyEmail email={c.email} /></span>}
                      </div>
                      {((c.partnering_mandates || []).length > 0 || (c.interest_areas || []).length > 0) && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {(c.interest_areas || []).map((a, i) => (
                            <Badge key={i} variant="secondary" className="text-[10px]">{a}</Badge>
                          ))}
                          {(c.partnering_mandates || []).length > 0 && (
                            <Badge
                              className="text-[10px] bg-violet-50 text-violet-700 border border-violet-200"
                              title={(c.partnering_mandates || []).map(m => `${m.section || m.therapeutic_area} — ${m.source_document}${m.page ? ` (p.${m.page})` : ''}`).join('\n')}
                              data-testid={`deck-badge-${c.id}`}
                            >
                              Partnering Deck ✓
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditEmployee(c); }}
                      title="Edit contact"
                      className="shrink-0 p-1.5 rounded-md text-slate-300 hover:text-sky-600 hover:bg-sky-100 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`edit-employee-${c.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteEmployee(c); }}
                      title="Delete contact (kept in suppression registry)"
                      className="shrink-0 p-1.5 rounded-md text-slate-300 hover:text-rose-600 hover:bg-rose-100 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-employee-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </a>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-400">No contacts from this company in your database yet</p>
                {profile?.moved_from && (
                  <Button
                    size="sm" variant="outline" className="h-7 text-[11px]"
                    disabled={recoveringContacts}
                    data-testid="recover-contacts-btn"
                    onClick={async () => {
                      setRecoveringContacts(true);
                      try {
                        const r = await axios.post(`${API}/company-profiles/${id}/recover-contacts`, {}, { withCredentials: true });
                        const n = r.data?.contacts_moved || 0;
                        toast[n ? 'success' : 'info'](n ? `Recovered ${n} contact${n === 1 ? '' : 's'} from previous classification` : 'No orphaned contacts found for this company');
                        if (n) fetchProfile();
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Recovery failed'); }
                      finally { setRecoveringContacts(false); }
                    }}
                  >
                    {recoveringContacts ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                    Recover contacts from previous classification
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

          <Dialog open={personReviewOpen} onOpenChange={setPersonReviewOpen}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="person-review-dialog">
              <DialogHeader>
                <DialogTitle>Possible existing employees — Review</DialogTitle>
                <DialogDescription>
                  These people were named in a document but could not be confidently matched. Merge into the suggested employee, create a new employee, or dismiss. Nothing is merged silently.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {(profile?.pending_person_matches || []).map((pm, i) => (
                  <div key={i} className="border border-slate-200 rounded-md p-3" data-testid={`pending-person-${i}`}>
                    <div className="text-sm font-semibold text-slate-800">{pm.name} <span className="text-xs font-normal text-slate-500">{pm.title}</span></div>
                    <div className="text-[11px] text-slate-400 mb-2">{pm.section ? `Section: ${pm.section} · ` : ''}{pm.document}</div>
                    {(pm.candidates || []).map((cand) => (
                      <div key={cand.id} className="flex items-center justify-between gap-2 py-1">
                        <span className="text-xs text-slate-600">{cand.name} {cand.email ? `· ${cand.email}` : ''} {cand.designation ? `· ${cand.designation}` : ''}</span>
                        <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={personResolving === pm.name}
                          data-testid={`merge-person-${i}-${cand.id}`}
                          onClick={() => resolvePerson(pm, 'merge', cand.id)}>Merge into this employee</Button>
                      </div>
                    ))}
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={personResolving === pm.name} data-testid={`create-person-${i}`} onClick={() => resolvePerson(pm, 'create')}>Create new employee</Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[11px] text-slate-400" disabled={personResolving === pm.name} onClick={() => resolvePerson(pm, 'dismiss')}>Dismiss</Button>
                    </div>
                  </div>
                ))}
                {(profile?.pending_person_matches || []).length === 0 && <p className="text-xs text-slate-400">Nothing pending.</p>}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* NDA & Agreements (between our firm and this pharma/distributor) */}
      <Card className="border border-slate-200 shadow-sm mb-8" data-testid="profile-agreements-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <FileSignature className="w-4 h-4 text-emerald-600" />
            <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
              NDA & Agreements
            </h3>
            <Badge variant="secondary" className="text-xs">{agreements.length}</Badge>
            <span className="text-[11px] text-slate-400 ml-2">Between our firm and {profile?.company_name || 'this counterparty'}</span>
          </div>

          {/* Existing entries */}
          {agreements.length > 0 ? (
            <div className="space-y-2 mb-4">
              {agreements.map(d => {
                const STATUS_COLORS = {
                  'Not Started': 'bg-slate-100 text-slate-600',
                  'Sent': 'bg-sky-100 text-sky-700',
                  'Under Review': 'bg-amber-100 text-amber-700',
                  'Signed': 'bg-emerald-100 text-emerald-700',
                  'Cancelled': 'bg-rose-100 text-rose-700',
                };
                const docColors = {
                  'NDA': 'bg-indigo-100 text-indigo-700',
                  'Service Agreement': 'bg-violet-100 text-violet-700',
                  'Other': 'bg-slate-100 text-slate-700',
                };
                const hasFile = !!d.storage_path;
                const displayLabel = d.doc_type === 'Other' && d.notes
                  ? (d.notes.split('—')[0] || 'Other').trim()
                  : d.doc_type;
                return (
                  <div
                    key={d.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 bg-white"
                    data-testid={`agreement-row-${d.id}`}
                  >
                    <Badge className={`text-[10px] px-2 py-0.5 ${docColors[d.doc_type] || 'bg-slate-100 text-slate-700'}`}>
                      {displayLabel}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      {hasFile ? (
                        <button
                          onClick={() => handleDownloadAgreement(d.id, d.original_filename)}
                          className="text-sm font-medium text-slate-900 hover:text-sky-600 truncate text-left"
                          data-testid={`agreement-download-${d.id}`}
                          title={d.original_filename}
                        >
                          {d.original_filename}
                        </button>
                      ) : (
                        <span className="text-sm text-slate-500 italic">No file yet — tracker only</span>
                      )}
                      <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                        {d.signed_date && <span>Signed {d.signed_date}</span>}
                        {d.signed_date && d.notes && <span>·</span>}
                        {d.notes && d.doc_type !== 'Other' && <span className="truncate italic">{d.notes}</span>}
                        {d.doc_type === 'Other' && d.notes && d.notes.includes('—') && (
                          <span className="truncate italic">{d.notes.split('—').slice(1).join('—').trim()}</span>
                        )}
                      </div>
                    </div>
                    <Select
                      value={d.status || 'Not Started'}
                      onValueChange={v => handleAgreementStatusChange(d.id, v)}
                    >
                      <SelectTrigger
                        className={`w-[130px] h-7 text-[11px] border ${STATUS_COLORS[d.status] || 'bg-slate-50 text-slate-600'}`}
                        data-testid={`agreement-status-${d.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['Not Started', 'Sent', 'Under Review', 'Signed', 'Cancelled'].map(s => (
                          <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {hasFile && (
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 w-7 p-0 text-slate-400 hover:text-sky-600"
                        onClick={() => handleDownloadAgreement(d.id, d.original_filename)}
                        title="Download"
                        data-testid={`agreement-download-btn-${d.id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
                      onClick={() => handleDeleteAgreement(d.id, displayLabel)}
                      title="Delete entry"
                      data-testid={`agreement-delete-${d.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-slate-400 mb-4">
              <FileSignature className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No NDAs or agreements tracked yet
            </div>
          )}

          {/* Add new row */}
          <div className="border-t border-slate-100 pt-4 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={agreementDocType} onValueChange={setAgreementDocType}>
                <SelectTrigger className="w-[170px] h-8 text-xs" data-testid="agreement-doctype-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NDA" className="text-xs">NDA / CDA</SelectItem>
                  <SelectItem value="Service Agreement" className="text-xs">Service Agreement</SelectItem>
                  <SelectItem value="Other" className="text-xs">Other (custom)</SelectItem>
                </SelectContent>
              </Select>
              {agreementDocType === 'Other' && (
                <Input
                  value={agreementCustomLabel}
                  onChange={e => setAgreementCustomLabel(e.target.value)}
                  placeholder="Custom label (e.g. MSA, SOW…)"
                  className="w-[200px] h-8 text-xs"
                  data-testid="agreement-custom-label-input"
                />
              )}
              <Select value={agreementStatus} onValueChange={setAgreementStatus}>
                <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="agreement-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['Not Started', 'Sent', 'Under Review', 'Signed', 'Cancelled'].map(s => (
                    <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={agreementSignedDate}
                onChange={e => setAgreementSignedDate(e.target.value)}
                className="w-[150px] h-8 text-xs"
                placeholder="Signed date"
                data-testid="agreement-signed-date-input"
              />
            </div>
            <Input
              value={agreementNotes}
              onChange={e => setAgreementNotes(e.target.value)}
              placeholder="Notes (optional — e.g. valid for 3 years, mutual, etc.)"
              className="h-8 text-xs"
              data-testid="agreement-notes-input"
            />
            <div className="flex items-center gap-2">
              <input
                ref={agreementFileRef}
                type="file"
                accept=".pdf,.doc,.docx"
                className="hidden"
                onChange={handleAgreementUpload}
                disabled={agreementUploading}
                data-testid="agreement-file-input"
              />
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5"
                onClick={() => agreementFileRef.current?.click()}
                disabled={agreementUploading}
                data-testid="agreement-upload-btn"
              >
                {agreementUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {agreementUploading ? 'Uploading…' : `Upload ${agreementDocType === 'Other' ? (agreementCustomLabel.trim() || 'File') : agreementDocType}`}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5 text-slate-600"
                onClick={() => handleAgreementUpload({ target: { files: [] } })}
                disabled={agreementUploading}
                data-testid="agreement-tracker-only-btn"
                title="Create a tracker entry without uploading a file yet (e.g. NDA still being drafted)"
              >
                <Plus className="w-3.5 h-3.5" />
                Add tracker (no file)
              </Button>
              <span className="text-[10px] text-slate-400 ml-1">PDF / DOC / DOCX</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Corporate Presentations */}
      <Card className="border border-slate-200 shadow-sm mb-8" data-testid="profile-decks-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-violet-500" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Corporate Presentations</h3>
              <Badge variant="secondary" className="text-[10px]">{(profile.corporate_decks || []).length}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <label className="cursor-pointer">
                <input type="file" accept=".pdf,.ppt,.pptx" className="hidden" onChange={handleDeckUpload} disabled={deckUploading} data-testid="deck-upload-input" />
                <Button variant="outline" size="sm" className="gap-1.5 text-xs pointer-events-none" disabled={deckUploading} data-testid="deck-upload-btn" asChild>
                  <span>
                    {deckUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {deckUploading ? 'Uploading...' : 'Upload Deck'}
                  </span>
                </Button>
              </label>
              <PharmaEnrichButton
                entityType="company_profile"
                entityId={id}
                hasDeck={(profile.corporate_decks || []).length > 0}
                onDone={fetchProfile}
              />
            </div>
          </div>
          {(profile.corporate_decks || []).length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {profile.corporate_decks.map((deck, i) => (
                <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3.5 group border border-slate-100" data-testid={`deck-item-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-violet-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{deck.filename}</p>
                    <p className="text-[10px] text-slate-400">
                      {(deck.size / 1024 / 1024).toFixed(1)} MB &middot; {new Date(deck.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/company-profiles/${profile.id}/download-deck/${encodeURIComponent(deck.filename)}`, { responseType: 'blob', withCredentials: true });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = deck.filename;
                        link.click();
                        window.URL.revokeObjectURL(url);
                      } catch { toast.error('Failed to download file'); }
                    }} className="text-xs font-medium text-sky-600 hover:text-sky-700 hover:underline px-2 py-1.5 rounded-md hover:bg-sky-50 transition-colors cursor-pointer" data-testid={`deck-view-${i}`}>
                      Download
                    </button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => handleDeleteDeck(deck.filename)} data-testid={`deck-delete-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <FileText className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No corporate presentations uploaded yet</p>
              <p className="text-[10px] text-slate-300 mt-1">Upload PDF or PowerPoint files</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Outreach / Deal Involvement */}
      <Card className="border border-slate-200 shadow-sm" data-testid="profile-outreach-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-4 h-4 text-sky-500" />
            <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Outreach & Deal Involvement</h3>
            <Badge variant="secondary" className="text-xs">{outreach.length}</Badge>
          </div>

          {outreach.length > 0 ? (
            <div className="border border-slate-100 rounded-lg overflow-hidden">
              <div className="grid grid-cols-[1fr_1fr_1fr_1.2fr_0.7fr] px-4 py-2 bg-slate-50 border-b border-slate-100">
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Biotech Company</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Asset</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Contact</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Status</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Date</span>
              </div>
              <div className="divide-y divide-slate-50 max-h-[500px] overflow-y-auto">
                {outreach.map((o) => (
                  <div key={o.id} className="grid grid-cols-[1fr_1fr_1fr_1.2fr_0.7fr] px-4 py-3 items-start gap-2 hover:bg-slate-50/50" data-testid={`outreach-row-${o.id}`}>
                    <div>
                      <a href={`/biotech/${o.biotech_company_id}`} target="_blank" rel="noopener noreferrer"
                        className="text-xs font-medium text-sky-700 hover:text-sky-900 hover:underline" data-testid={`outreach-biotech-link-${o.id}`}>
                        {o.biotech_company_name || '—'}
                      </a>
                    </div>
                    <div>
                      <p className="text-xs text-slate-700">{o.asset_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-900">{o.contact_name}</p>
                      {o.designation && <p className="text-[10px] text-slate-400">{o.designation}</p>}
                      {o.contact_email && <p className="text-[10px] text-slate-400 truncate">{o.contact_email}</p>}
                    </div>
                    <div>
                      <Badge className={`text-[10px] w-fit ${OUTREACH_COLORS[o.status] || 'bg-slate-100 text-slate-600'}`}>{o.status}</Badge>
                      {o.status_notes && (
                        <p className={`text-[10px] italic mt-1 leading-tight ${o.status === 'Declined' ? 'text-rose-500' : o.status === 'Awaiting Trial Data' ? 'text-orange-500' : 'text-slate-500'}`}>{o.status_notes}</p>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 whitespace-nowrap" data-testid={`outreach-date-${o.id}`} title={o.created_at ? new Date(o.created_at).toLocaleString() : ''}>
                      {o.created_at ? new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Send className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No outreach entries linked to employees of this company</p>
              <p className="text-xs text-slate-400 mt-1">Outreach entries appear when contacts from this company are tagged in biotech outreach trackers</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Employee Dialog */}
      <Dialog open={employeeDialogOpen} onOpenChange={(open) => { setEmployeeDialogOpen(open); if (!open) { setEmployeeForm({ ...emptyEmployee }); setEditingEmployee(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editingEmployee ? `Edit Contact — ${editingEmployee.name}` : 'Add Employee'}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Add a new contact at {profile?.company_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddEmployee} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={employeeForm.name} onChange={e => setEmployeeForm(p => ({ ...p, name: e.target.value }))} required placeholder="e.g. John Smith" data-testid="employee-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Designation</Label>
                <Input value={employeeForm.designation} onChange={e => setEmployeeForm(p => ({ ...p, designation: e.target.value }))} placeholder="e.g. Head of BD" data-testid="employee-designation-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={employeeForm.email} onChange={e => setEmployeeForm(p => ({ ...p, email: e.target.value }))} placeholder="john@company.com" data-testid="employee-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={employeeForm.phone} onChange={e => setEmployeeForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 234 567 890" data-testid="employee-phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Input value={employeeForm.department} onChange={e => setEmployeeForm(p => ({ ...p, department: e.target.value }))} placeholder="e.g. Business Development" data-testid="employee-department-input" />
            </div>
            <div className="space-y-2">
              <Label>Country</Label>
              <CountrySelect value={employeeForm.location} onChange={v => setEmployeeForm(p => ({ ...p, location: v }))} testId="employee-country-select" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setEmployeeDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" disabled={!employeeForm.name} data-testid="submit-employee-btn">{editingEmployee ? 'Save Changes' : 'Add Employee'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* BD Routing Policy Dialog */}
      <Dialog open={policyDialogOpen} onOpenChange={setPolicyDialogOpen}>
        <DialogContent className="max-w-md" data-testid="routing-policy-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>BD Routing Policy</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Lock partnering outreach for <span className="font-semibold">{profile?.company_name}</span> to a single designated contact. When enabled, ForesightRx will block emails to any other contact at this company.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Mode</Label>
              <Select value={policyForm.mode} onValueChange={(val) => setPolicyForm(p => ({ ...p, mode: val }))}>
                <SelectTrigger data-testid="policy-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All contacts (open routing)</SelectItem>
                  <SelectItem value="single_poc">Single Point of Contact (locked to one person/inbox)</SelectItem>
                  <SelectItem value="portal_only">Portal Submission Only (no direct email)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {policyForm.mode === 'single_poc' && (
              <>
                {contacts.length > 0 && (
                  <div className="space-y-2">
                    <Label>Pick from existing contacts (optional)</Label>
                    <Select value={policyForm.poc_email} onValueChange={handlePickPocFromContacts}>
                      <SelectTrigger data-testid="policy-pick-contact-select"><SelectValue placeholder="Select a contact..." /></SelectTrigger>
                      <SelectContent>
                        {contacts.filter(c => c.email).map(c => (
                          <SelectItem key={c.id} value={c.email}>{c.name} — {c.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>POC Email *</Label>
                  <Input
                    type="email"
                    value={policyForm.poc_email}
                    onChange={(e) => setPolicyForm(p => ({ ...p, poc_email: e.target.value }))}
                    placeholder="e.g. partnering@company.com"
                    data-testid="policy-poc-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>POC Name</Label>
                  <Input
                    value={policyForm.poc_name}
                    onChange={(e) => setPolicyForm(p => ({ ...p, poc_name: e.target.value }))}
                    placeholder="e.g. Eunice Ng or Business Development Inbox"
                    data-testid="policy-poc-name-input"
                  />
                </div>
              </>
            )}
            {policyForm.mode === 'portal_only' && (
              <>
                <div className="space-y-2">
                  <Label>Portal URL *</Label>
                  <Input
                    type="url"
                    value={policyForm.portal_url}
                    onChange={(e) => setPolicyForm(p => ({ ...p, portal_url: e.target.value }))}
                    placeholder="https://novartis.partneringplace.com/OppPortal/portal/Novartis"
                    data-testid="policy-portal-url-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Portal Display Name</Label>
                  <Input
                    value={policyForm.portal_name}
                    onChange={(e) => setPolicyForm(p => ({ ...p, portal_name: e.target.value }))}
                    placeholder="e.g. Novartis Partnering Portal"
                    data-testid="policy-portal-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reference Contact Name (optional — who informed us)</Label>
                  <Input
                    value={policyForm.poc_name}
                    onChange={(e) => setPolicyForm(p => ({ ...p, poc_name: e.target.value }))}
                    placeholder="e.g. Frank Dieterle"
                    data-testid="policy-portal-contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reference Contact Email (optional)</Label>
                  <Input
                    type="email"
                    value={policyForm.poc_email}
                    onChange={(e) => setPolicyForm(p => ({ ...p, poc_email: e.target.value }))}
                    placeholder="e.g. frank.dieterle@novartis.com"
                    data-testid="policy-portal-contact-email-input"
                  />
                </div>
              </>
            )}
            {(policyForm.mode === 'single_poc' || policyForm.mode === 'portal_only') && (
              <div className="space-y-2">
                <Label>Note (shown as a warning banner)</Label>
                <textarea
                  className="w-full text-sm border border-slate-200 rounded-md p-2 resize-y min-h-[60px] focus:outline-none focus:ring-1 focus:ring-sky-300"
                  value={policyForm.note}
                  onChange={(e) => setPolicyForm(p => ({ ...p, note: e.target.value }))}
                  placeholder="e.g. Novartis requires all BD proposals through the portal (Frank Dieterle, Apr 2026)."
                  data-testid="policy-note-input"
                />
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setPolicyDialogOpen(false)} disabled={savingPolicy}>Cancel</Button>
              <Button
                type="button"
                className="bg-amber-600 text-white hover:bg-amber-700"
                onClick={handleSavePolicy}
                disabled={savingPolicy || (policyForm.mode === 'single_poc' && !policyForm.poc_email.trim()) || (policyForm.mode === 'portal_only' && !policyForm.portal_url.trim())}
                data-testid="save-routing-policy-btn"
              >
                {savingPolicy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</> : 'Save Policy'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stage Gate Dialog */}
      <Dialog open={stageGateDialogOpen} onOpenChange={setStageGateDialogOpen}>
        <DialogContent className="max-w-md" data-testid="stage-gate-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Stage Gate</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Set the minimum development stage this pharma accepts for in-licensing.
              Assets below this stage will be excluded from AI Mapping for <span className="font-semibold">{profile?.company_name}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-medium text-slate-700">Minimum Stage</Label>
              <select
                value={stageGateForm.min_stage}
                onChange={e => setStageGateForm(prev => ({ ...prev, min_stage: e.target.value }))}
                className="mt-1 w-full h-9 border border-slate-300 rounded-md px-3 text-sm bg-white"
                data-testid="stage-gate-min-stage"
              >
                <option value="">— No restriction —</option>
                <option value="Preclinical">Preclinical+</option>
                <option value="IND-enabling">IND-enabling+</option>
                <option value="Phase 1">Phase 1+</option>
                <option value="Phase 2">Phase 2+</option>
                <option value="Phase 3">Phase 3+</option>
                <option value="Filed">Filed (NDA/BLA)+</option>
                <option value="Approved">Approved only</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Note / Source</Label>
              <Textarea
                value={stageGateForm.note}
                onChange={e => setStageGateForm(prev => ({ ...prev, note: e.target.value }))}
                placeholder='e.g. "Pharma only in-licenses Phase 3+ per CBO feedback Apr 2026"'
                className="mt-1 text-sm min-h-[72px]"
                data-testid="stage-gate-note"
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            {profile?.stage_gate?.min_stage ? (
              <Button variant="outline" size="sm" onClick={handleDeleteStageGate} disabled={savingStageGate}
                className="h-8 text-xs text-slate-600 border-slate-300 hover:bg-slate-50" data-testid="stage-gate-remove-btn">
                Remove Gate
              </Button>
            ) : <div />}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setStageGateDialogOpen(false)} className="h-8 text-xs" data-testid="stage-gate-cancel-btn">Cancel</Button>
              <Button size="sm" onClick={handleSaveStageGate} disabled={savingStageGate}
                className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="stage-gate-save-btn">
                {savingStageGate ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Saving…</> : 'Save Gate'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Franchise Anchors Dialog (v27) */}
      <Dialog open={anchorDialogOpen} onOpenChange={setAnchorDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="franchise-anchor-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Manual Franchise Anchors</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Type the real marketed / late-stage products <span className="font-semibold">{profile?.company_name}</span> owns per therapeutic area. The Matchmaker uses these to write evidence-anchored rationales when Deep Research hasn&rsquo;t populated them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            {anchorRows.map((row, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-2 items-start">
                <input
                  type="text"
                  value={row.ta}
                  onChange={e => setAnchorRows(rows => rows.map((r, i) => i === idx ? { ...r, ta: e.target.value } : r))}
                  placeholder="Therapeutic Area (e.g. Oncology)"
                  className="h-9 border border-slate-300 rounded-md px-3 text-sm bg-white"
                  data-testid={`anchor-ta-${idx}`}
                />
                <input
                  type="text"
                  value={row.products_text}
                  onChange={e => setAnchorRows(rows => rows.map((r, i) => i === idx ? { ...r, products_text: e.target.value } : r))}
                  placeholder="Product names, comma-separated (e.g. Keytruda, Lynparza)"
                  className="h-9 border border-slate-300 rounded-md px-3 text-sm bg-white"
                  data-testid={`anchor-products-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => setAnchorRows(rows => rows.filter((_, i) => i !== idx))}
                  className="h-9 px-2 text-xs text-rose-700 hover:bg-rose-50 rounded-md border border-rose-200"
                  data-testid={`anchor-delete-${idx}`}
                >Delete</button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setAnchorRows(rows => [...rows, { ta: '', products_text: '' }])}
              className="text-xs font-medium text-emerald-800 hover:bg-emerald-50 border border-emerald-300 px-3 py-1.5 rounded-md"
              data-testid="anchor-add-row"
            >+ Add another therapeutic area</button>
            <div>
              <Label className="text-xs font-medium text-slate-700">Note / Source (optional)</Label>
              <Textarea
                value={anchorNote}
                onChange={e => setAnchorNote(e.target.value)}
                placeholder='e.g. "Added manually per Q1 2026 BD lead review"'
                className="mt-1 text-sm min-h-[64px]"
                data-testid="anchor-note"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setAnchorDialogOpen(false)} className="h-8 text-xs" data-testid="anchor-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={handleSaveAnchors} disabled={savingAnchors}
              className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="anchor-save-btn">
              {savingAnchors ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Saving…</> : 'Save Anchors'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Log Emerging Interest Dialog */}
      <Dialog open={signalDialogOpen} onOpenChange={setSignalDialogOpen}>
        <DialogContent className="max-w-md" data-testid="signal-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Log Emerging Interest</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Capture a revealed preference from <span className="font-semibold">{profile?.company_name}</span> — paste a BD reply or email and the AI will detect what they ARE and AREN'T interested in.
            </DialogDescription>
          </DialogHeader>

          {/* AI-paste mode */}
          <div className="space-y-2 mt-2 p-3 rounded-md bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
            <Label className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide flex items-center gap-1.5">
              <Search className="w-3 h-3" /> Paste email or statement — AI auto-detects
            </Label>
            <textarea
              className="w-full text-xs border border-amber-200 rounded-md p-2 resize-y min-h-[120px] bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
              value={signalPasteText}
              onChange={(e) => setSignalPasteText(e.target.value)}
              placeholder={"e.g. \"Dear Amit, Thank you for introducing this opportunity. Unfortunately, dry eye disease is outside of our current focus area, as we are concentrating on retinal diseases. Therefore, we would like to pass on this opportunity.\""}
              disabled={parsingSignal}
              data-testid="signal-paste-textarea"
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleParseSignal}
                disabled={parsingSignal || !signalPasteText.trim()}
                className="h-8 text-xs bg-amber-600 text-white hover:bg-amber-700"
                data-testid="parse-signal-btn"
              >
                {parsingSignal ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Parsing with AI…</> : <>Parse & Save</>}
              </Button>
            </div>
            <p className="text-[10px] text-amber-700/80 leading-tight">
              The AI extracts both "interested in" (used to boost matches) and "not interested in" (filters them out of future AI Mapping for that area).
            </p>
          </div>

          <div className="relative my-3">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-2 text-[10px] uppercase tracking-wider text-slate-400">or log manually</span></div>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Therapeutic Area *</Label>
              <Input
                value={signalForm.area}
                onChange={(e) => setSignalForm(p => ({ ...p, area: e.target.value }))}
                placeholder="e.g. Ophthalmology"
                data-testid="signal-area-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={signalForm.status} onValueChange={(v) => setSignalForm(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="signal-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="under_review">Under Review (exploring)</SelectItem>
                  <SelectItem value="active_scouting">Active Scouting (talking to companies)</SelectItem>
                  <SelectItem value="confirmed_interest">Confirmed Interest (committed)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {contacts.length > 0 && (
              <div className="space-y-2">
                <Label>Source Contact (who revealed this?)</Label>
                <Select value={signalForm.source_contact_id} onValueChange={handlePickSourceContact}>
                  <SelectTrigger data-testid="signal-source-select"><SelectValue placeholder="Pick a contact..." /></SelectTrigger>
                  <SelectContent>
                    {contacts.filter(c => c.name).map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}{c.designation ? ` — ${c.designation}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Evidence / Quote</Label>
              <textarea
                className="w-full text-sm border border-slate-200 rounded-md p-2 resize-y min-h-[70px] focus:outline-none focus:ring-1 focus:ring-amber-300"
                value={signalForm.evidence}
                onChange={(e) => setSignalForm(p => ({ ...p, evidence: e.target.value }))}
                placeholder="e.g. Email reply from Laura Bloomberg dated Apr 21, 2026: 'Our new areas team is currently reviewing the ophthalmology space.'"
                data-testid="signal-evidence-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setSignalDialogOpen(false)} disabled={savingSignal}>Cancel</Button>
              <Button
                type="button"
                className="bg-amber-600 text-white hover:bg-amber-700"
                onClick={handleSaveSignal}
                disabled={savingSignal || !signalForm.area.trim()}
                data-testid="save-signal-btn"
              >
                {savingSignal ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</> : 'Log Signal'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Match Assets by Prompt — company-level scouting */}
      <Dialog open={matchDialogOpen} onOpenChange={setMatchDialogOpen}>
        <DialogContent className="sm:max-w-3xl" data-testid="match-by-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-4 h-4 text-sky-600" />
              Match Assets — {profile.company_name}
            </DialogTitle>
            <DialogDescription>
              Describe what this {profile.category === 'regional_distributor' ? 'partner' : 'pharma'} is scouting (therapeutic area, stage, modality, geography). Claude extracts keywords and ranks matching biotech assets from your CRM.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={matchPrompt}
              onChange={(e) => setMatchPrompt(e.target.value)}
              placeholder="e.g. Phase II ophthalmology assets, preferably small molecules, for in-licensing in Central & Eastern Europe."
              rows={4}
              data-testid="match-prompt-input"
            />
            {(profile.therapeutic_interests || []).length > 0 && (
              <p className="text-[11px] text-slate-500">
                Stored interests will narrow matches: <span className="font-medium text-slate-600">{(profile.therapeutic_interests || []).join(', ')}</span>
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setMatchDialogOpen(false)} disabled={matchRunning}>Close</Button>
              <Button
                onClick={runMatchByPrompt}
                disabled={matchRunning || !matchPrompt.trim()}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="run-match-btn"
              >
                {matchRunning ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Matching…</> : <><Search className="w-3.5 h-3.5 mr-1.5" /> Find Matching Assets</>}
              </Button>
            </div>

            {matchResults && (matchResults.matches || []).length > 0 && (
              <div className="border-t border-slate-100 pt-3" data-testid="match-results">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-700">
                    Top matches <span className="text-slate-400">({matchResults.total_matches})</span>
                  </span>
                  {matchResults.extracted?.focus_phrase && (
                    <span className="text-[10px] text-slate-400 italic">extracted: "{matchResults.extracted.focus_phrase}"</span>
                  )}
                </div>
                <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-100 border border-slate-100 rounded-md">
                  {(matchResults.matches || []).slice(0, 50).map((m, i) => (
                    <div key={`${m.asset_id}-${i}`} className="px-3 py-2 hover:bg-slate-50/60 flex items-start justify-between gap-3" data-testid={`match-row-${i}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 truncate">{m.asset_name || '(unnamed asset)'}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {m.company_name && <span className="font-medium text-slate-600">{m.company_name}</span>}
                          {m.stage && <> · {m.stage}</>}
                          {m.indication && <> · <span className="truncate">{m.indication}</span></>}
                        </div>
                        {bestContacts[m.asset_id] && (
                          <div className="mt-1.5 space-y-0.5" data-testid={`best-contacts-${i}`}>
                            {(bestContacts[m.asset_id].excluded_contacts || []).map((bc) => (
                              <div key={bc.contact_id} className="text-[11px] text-rose-500" title={bc.exclusion_reason}>
                                ✕ <a href={`/pharma/${bc.contact_id}`} target="_blank" rel="noreferrer" className="font-medium text-rose-600 hover:underline">{bc.name}</a>
                                <span> — excluded: {bc.exclusion_reason}</span>
                              </div>
                            ))}
                            {(bestContacts[m.asset_id].contacts || []).slice(0, 3).map((bc) => (
                              <div key={bc.contact_id} className="text-[11px] text-slate-600" title={(bc.reasons || []).join(' · ')}>
                                ★ <a href={`/pharma/${bc.contact_id}`} target="_blank" rel="noreferrer" className="font-medium text-sky-700 hover:underline">{bc.name}</a>
                                <span> — {bc.score}% · {(bc.reasons || [])[0] || ''}</span>
                              </div>
                            ))}
                            {(bestContacts[m.asset_id].contacts || []).length === 0 && (bestContacts[m.asset_id].excluded_contacts || []).length === 0 && (
                              <div className="text-[11px] text-slate-400">No contact-level mandate match — route via company-level fit</div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {m.asset_id && (
                          <button
                            className="text-[11px] text-violet-600 hover:underline"
                            data-testid={`best-contacts-btn-${i}`}
                            onClick={async () => {
                              try {
                                const r = await axios.get(`${API}/company-profiles/${id}/best-contacts?asset_id=${m.asset_id}`, { withCredentials: true });
                                setBestContacts(prev => ({ ...prev, [m.asset_id]: r.data }));
                              } catch { toast.error('Could not rank contacts'); }
                            }}
                          >
                            Best contacts
                          </button>
                        )}
                        {m.company_id && (
                          <a
                            href={`/company/${m.company_id}`}
                            target="_blank" rel="noreferrer"
                            className="text-[11px] text-sky-600 hover:underline"
                            data-testid={`match-open-company-${i}`}
                          >
                            Open ↗
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {(matchResults.total_matches || 0) > 50 && (
                  <p className="text-[10px] text-slate-400 mt-2">Showing first 50 of {matchResults.total_matches}. Refine the prompt to narrow further.</p>
                )}
              </div>
            )}

            {matchResults && (matchResults.matches || []).length === 0 && (
              <div className="border-t border-slate-100 pt-3 text-xs text-slate-500" data-testid="match-no-results">
                No assets matched. Try broadening the therapeutic area, dropping the stage constraint, or rephrasing the modality.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge Pharma Company — consolidate duplicate profiles (e.g.
          "Cipla" / "Cipla Ltd" / "Cipla US" → single "Cipla" master).
          Mirrors the biotech CompanyDetailPage merge UX exactly so the
          two sides stay consistent. Feb 2026. */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="pharma-merge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="w-5 h-5 text-indigo-600" /> Merge into {profile?.company_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-indigo-200 bg-indigo-50 p-2.5 text-[11px] text-indigo-900">
              <strong>Master (this record):</strong> {profile?.company_name}
              <div className="text-indigo-700 mt-0.5">
                All employees, contacts, therapeutic interests, BD routing rules, outreach, notes and metadata from the picked duplicates will be merged into this master. Nothing is lost — an audit snapshot is saved for rollback.
              </div>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Search for duplicates</label>
              <Input
                type="text"
                value={mergeQuery}
                onChange={e => setMergeQuery(e.target.value)}
                placeholder="Start typing a company name (e.g. Cipla)…"
                className="mt-1 text-[12px]"
                data-testid="pharma-merge-search"
                autoFocus
              />
            </div>
            {mergeSearching && (
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                <Loader2 className="w-3 h-3 animate-spin" /> Searching pharma database…
              </div>
            )}
            {mergeResults.length > 0 && (
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-64 overflow-y-auto" data-testid="pharma-merge-results">
                {mergeResults.map(p => {
                  const checked = !!mergePicked[p.id];
                  return (
                    <label
                      key={p.id}
                      className={`flex items-center gap-2 px-2.5 py-2 cursor-pointer text-[12px] ${checked ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
                      data-testid={`pharma-merge-opt-${p.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setMergePicked(prev => {
                            const next = { ...prev };
                            if (next[p.id]) delete next[p.id];
                            else next[p.id] = p;
                            return next;
                          });
                        }}
                        className="w-3.5 h-3.5 accent-indigo-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{p.company_name}</div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {p.hq_location || p.headquarters || '—'}
                          {p.partnering_email ? ` · ${p.partnering_email}` : ''}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            {Object.keys(mergePicked).length > 0 && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5" data-testid="pharma-merge-preview">
                <div className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700">Will merge {Object.keys(mergePicked).length} record{Object.keys(mergePicked).length === 1 ? '' : 's'} into master</div>
                <ul className="mt-1 space-y-0.5 text-[11px] text-emerald-800">
                  {Object.values(mergePicked).map(p => (
                    <li key={p.id} className="flex items-center gap-1.5">
                      <span className="text-emerald-600">✓</span> {p.company_name}
                      <button
                        type="button"
                        onClick={() => setMergePicked(prev => { const n = { ...prev }; delete n[p.id]; return n; })}
                        className="ml-auto text-emerald-500 hover:text-rose-500"
                        data-testid={`pharma-merge-unpick-${p.id}`}
                        title="Remove from merge"
                      >
                        <IconX className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => setMergeDialogOpen(false)} disabled={merging} data-testid="pharma-merge-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmPharmaMerge}
              disabled={merging || Object.keys(mergePicked).length === 0}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="pharma-merge-confirm"
            >
              {merging
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Merging…</>
                : <><GitMerge className="w-3.5 h-3.5 mr-1.5" /> Merge {Object.keys(mergePicked).length || ''} into master</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * PerTAContactsCard — aggregates every employee's `interest_areas` into
 * a TA → people map so BD can see the P3 (individual tagged interests)
 * layer right next to the P2 mailbox routing rules.
 *
 * Zero backend change — pure derivation over the `contacts` array the
 * parent already fetched.
 */
function PerTAContactsCard({ contacts, profile, onPromoted }) {
  const [expanded, setExpanded] = useState(true);
  const [promoting, setPromoting] = useState(null); // `${ta}|${contactId}` while saving
  // One-click promotion: tag-guess → confirmed explicit rule (Aug 2026).
  const promoteToRule = async (row, person) => {
    const key = `${row.label}|${person.id}`;
    if (promoting || !profile?.id) return;
    if (!window.confirm(`Create an explicit rule: ${row.label} opportunities → ${person.name || person.email}?\n\nExplicit rules always win over tag-based fallback.`)) return;
    setPromoting(key);
    try {
      await axios.post(
        `${API}/company-profiles/${profile.id}/bd-routing-rules`,
        {
          therapeutic_areas: Array.from(new Set([row.label, ...row.variants])),
          route_to: [{ contact_id: person.id, name: person.name, email: person.email, designation: person.designation }],
          bypass: [],
          note: `Promoted from contact interest tags (${row.variants.length > 1 ? row.variants.join(' / ') : row.label})`,
        },
        { withCredentials: true },
      );
      toast.success(`Explicit rule created: ${row.label} → ${person.name || person.email}`);
      onPromoted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create rule');
    } finally {
      setPromoting(null);
    }
  };
  // Aug 2026 (v2) · TA-first consolidated view (user preference): one row
  // per therapeutic area → the people who receive outreach for it.
  // Spelling variants ("CNS (Neurology)" / "CNS/Neurology" / "Neurology /
  // CNS") still collapse into a single TA row via token-set clustering.
  const taRows = React.useMemo(() => {
    const normKey = (ta) => Array.from(new Set(
      ta.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)
    )).sort().join('|');
    const clusters = new Map();
    // Sep 2026 fix: some imported contacts carry ONE comma-joined string
    // ("Immunology, Oncology, Neuroscience, …") — split into real tags so
    // they group under each TA instead of forming a combined pseudo-TA.
    const NOISE_TAGS = new Set(['multiple', 'various', 'other', 'others', 'general', 'n/a', 'na']);
    (contacts || []).forEach((c) => {
      const rawList = (c.interest_areas || []).flatMap((rawTa) =>
        String(rawTa || '').split(/[,;·|]+/).map(s => s.trim()).filter(Boolean)
      );
      Array.from(new Set(rawList)).forEach((ta) => {
        if (NOISE_TAGS.has(ta.toLowerCase())) return;
        const key = normKey(ta);
        const cl = clusters.get(key) || { label: ta, variants: [], people: new Map() };
        if (!cl.variants.includes(ta)) cl.variants.push(ta);
        if (ta.length < cl.label.length) cl.label = ta; // shortest = cleanest
        if (!cl.people.has(c.id)) {
          cl.people.set(c.id, { id: c.id, name: c.name || '', email: c.email || '', designation: c.designation || '' });
        }
        clusters.set(key, cl);
      });
    });
    return Array.from(clusters.values())
      .map(cl => ({ ...cl, people: Array.from(cl.people.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '')) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [contacts]);

  const totalPeople = new Set(taRows.flatMap(r => r.people.map(p => p.id))).size;
  const totalVariants = taRows.reduce((n, r) => n + r.variants.length, 0);
  const hasAny = taRows.length > 0;

  // Person-first pivot of the same data: one row per contact → their TAs.
  const [viewMode, setViewMode] = useState('ta'); // 'ta' | 'person'
  const personRows = React.useMemo(() => {
    const byId = new Map();
    taRows.forEach((row) => {
      row.people.forEach((p) => {
        const entry = byId.get(p.id) || { ...p, tas: [] };
        entry.tas.push({ label: row.label, variants: row.variants });
        byId.set(p.id, entry);
      });
    });
    return Array.from(byId.values()).sort((a, b) => b.tas.length - a.tas.length);
  }, [taRows]);

  return (
    <div
      className={`mb-6 rounded-b-lg border ${hasAny ? 'bg-indigo-50/40 border-indigo-200' : 'bg-slate-50 border-slate-200'}`}
      data-testid="per-ta-contacts-card"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left cursor-pointer"
      >
        <Users className={`w-4 h-4 shrink-0 ${hasAny ? 'text-indigo-600' : 'text-slate-400'}`} />
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider shrink-0">
          Tag-based recipients · auto-derived, manually editable below
        </span>
        {hasAny ? (
          <>
            <Badge variant="secondary" className="text-[10px] h-5">{taRows.length}</Badge>
            <span className="text-[11px] text-slate-500 ml-1">
              {taRows.length} TA{taRows.length === 1 ? '' : 's'} · {totalPeople} contact{totalPeople === 1 ? '' : 's'}
              {totalVariants > taRows.length && ` · ${totalVariants - taRows.length} spelling variants merged`}
            </span>
          </>
        ) : (
          <span className="text-[11px] text-slate-500 ml-1">
            No contact has TA interests tagged yet — edit an employee below to add interest areas.
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {hasAny && (
            <span
              className="flex items-center rounded-full border border-indigo-200 bg-white overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setViewMode('ta')}
                className={`px-2 py-0.5 text-[10px] font-semibold transition-colors ${viewMode === 'ta' ? 'bg-indigo-600 text-white' : 'text-indigo-600 hover:bg-indigo-50'}`}
                data-testid="fallback-view-by-ta"
              >
                By TA
              </button>
              <button
                type="button"
                onClick={() => setViewMode('person')}
                className={`px-2 py-0.5 text-[10px] font-semibold transition-colors ${viewMode === 'person' ? 'bg-indigo-600 text-white' : 'text-indigo-600 hover:bg-indigo-50'}`}
                data-testid="fallback-view-by-person"
              >
                By Person
              </button>
            </span>
          )}
          <span className="text-[11px] text-slate-400">{expanded ? 'Hide' : 'Show'}</span>
        </span>
      </div>

      {expanded && hasAny && viewMode === 'person' && (
        <div className="px-4 pb-3 space-y-1.5">
          {personRows.map((p) => (
            <div
              key={p.id}
              className="flex items-start gap-3 px-3 py-2 rounded border bg-white"
              data-testid={`fallback-person-row-${p.id}`}
            >
              <div className="shrink-0 min-w-[180px] max-w-[280px]">
                <div className="flex items-center gap-1">
                  <a
                    href={`/pharma/${p.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold text-slate-800 hover:text-sky-700 hover:underline truncate"
                    title={[p.designation, p.email].filter(Boolean).join(' · ')}
                  >
                    {p.name || p.email || '(unnamed)'}
                  </a>
                  {p.email && <CopyEmail email={p.email} />}
                </div>
                {p.email && <div className="text-[10px] text-slate-400 truncate">&lt;{p.email}&gt;</div>}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
                {p.tas.map((t) => (
                  <Badge
                    key={t.label}
                    className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px]"
                    title={t.variants.length > 1 ? `Also tagged as: ${t.variants.join(' · ')}` : undefined}
                  >
                    {t.label}
                    {t.variants.length > 1 && <span className="ml-1 opacity-60">×{t.variants.length}</span>}
                  </Badge>
                ))}
              </div>
              <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide text-indigo-500 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 mt-0.5">
                🏷 from tags
              </span>
            </div>
          ))}
          <div className="text-[11px] text-slate-500 ml-6 pt-1">
            Used ONLY when the {'"Explicit rules · from BD emails"'} above don&apos;t cover a TA. Hover a TA chip to see merged spelling variants. Edit a contact&apos;s interest areas in the Employees section below.
          </div>
        </div>
      )}

      {expanded && hasAny && viewMode === 'ta' && (
        <div className="px-4 pb-3 space-y-1.5">
          {taRows.map((row) => (
            <div
              key={row.label}
              className="flex items-start gap-2 px-3 py-2 rounded border bg-white"
              data-testid={`per-ta-row-${row.label}`}
            >
              <Badge
                className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px] shrink-0"
                title={row.variants.length > 1 ? `Also tagged as: ${row.variants.join(' · ')}` : undefined}
              >
                {row.label}
                {row.variants.length > 1 && <span className="ml-1 opacity-60">×{row.variants.length}</span>}
              </Badge>
              <span className="text-[10px] text-slate-400 shrink-0 mt-0.5">{row.people.length}</span>
              <div className="flex items-center gap-x-2 gap-y-1 flex-wrap min-w-0 flex-1">
                {row.people.map((p, i) => (
                  <span key={p.id + i} className="flex items-center gap-1 min-w-0">
                    {i > 0 && <span className="text-[10px] text-slate-300">·</span>}
                    <a
                      href={`/pharma/${p.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-slate-800 hover:text-sky-700 hover:underline truncate"
                      title={[p.designation, p.email].filter(Boolean).join(' · ')}
                    >
                      {p.name || p.email || '(unnamed)'}
                    </a>
                    {p.email && (
                      <span className="text-[10px] text-slate-400 truncate hidden lg:inline">
                        &lt;{p.email}&gt;
                      </span>
                    )}
                    {p.email && <CopyEmail email={p.email} />}
                    <button
                      type="button"
                      onClick={() => promoteToRule(row, p)}
                      disabled={promoting === `${row.label}|${p.id}`}
                      className="ml-0.5 shrink-0 rounded-full border border-violet-300 bg-violet-50 px-1.5 py-[1px] text-[9px] font-semibold text-violet-700 hover:bg-violet-600 hover:text-white transition-colors disabled:opacity-50"
                      title={`Promote to explicit rule: ${row.label} → ${p.name || p.email}`}
                      data-testid={`promote-rule-${row.label}-${p.id}`}
                    >
                      {promoting === `${row.label}|${p.id}` ? '…' : '↑ rule'}
                    </button>
                  </span>
                ))}
              </div>
              <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide text-indigo-500 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 mt-0.5">
                🏷 from tags
              </span>
            </div>
          ))}
          <div className="text-[11px] text-slate-500 ml-6 pt-1">
            Used ONLY when the {'"Explicit rules · from BD emails"'} above don&apos;t cover a TA. Hover a TA chip to see merged spelling variants. Edit a contact&apos;s interest areas in the Employees section below.
          </div>
        </div>
      )}
    </div>
  );
}

